<template>
	<div class="ged-wrapper">
		<notifications position="top center" group="info" />

		<!-- ── En-tête ─────────────────────────────────────────────── -->
		<div v-if="!select" class="mb-3">
			<h4 class="mb-0 font-weight-bold">
				<i class="fas fa-folder-open text-warning mr-2"></i>Documents
			</h4>
		</div>
		<b-button v-if="select && minimal" v-b-toggle="'doc-' + id" variant="outline-secondary" size="sm">
			<i class="fas fa-folder mr-1"></i>Document
		</b-button>

		<b-collapse visible :id="'doc-' + id">

			<!-- ── Onglets Mes docs / Partagés ──────────────────────── -->
			<b-tabs v-if="share_rules && !minimal" pills class="mb-3" @input="onShareTabChange">
				<b-tab title="Mes documents"    :active="!share"></b-tab>
				<b-tab title="Documents partagés" :active="share"></b-tab>
			</b-tabs>

			<!-- ── Barre d'outils unique ───────────────────────────────── -->
			<b-card no-body class="mb-3 shadow-sm">
				<b-card-body class="py-2 px-3">
					<div class="d-flex align-items-center flex-wrap" style="gap:.5rem">

						<!-- Navigation / breadcrumb (mode dossiers) -->
						<template v-if="!document_tags">
							<b-breadcrumb class="mb-0 bg-transparent p-0 flex-shrink-1" style="font-size:.9rem; min-width:0">
								<b-breadcrumb-item @click="parent_folder(true)" :active="folders.length === 0">
									<i class="fas fa-home"></i>
								</b-breadcrumb-item>
								<b-breadcrumb-item
									v-for="(f, i) in folders.filter(Boolean)"
									:key="i"
									:active="i === folders.filter(Boolean).length - 1"
									@click="go_to_folder_idx(i)">
									{{ f.libelle }}
								</b-breadcrumb-item>
							</b-breadcrumb>
							<b-button
								v-if="folders.length > 0"
								size="sm"
								variant="outline-secondary"
								class="flex-shrink-0"
								v-b-tooltip.hover title="Dossier parent"
								@click="parent_folder(false)">
								<i class="fas fa-level-up-alt"></i>
							</b-button>
						</template>

						<!-- Mode tags actif : indicateur dans la zone de navigation -->
						<template v-else>
							<b-badge variant="info" class="px-3 py-2 flex-shrink-0">
								<i class="fas fa-tags mr-1"></i>Vue par tags
							</b-badge>
						</template>

						<!-- Actions groupées -->
						<div class="ml-auto d-flex align-items-center flex-shrink-0" style="gap:.5rem">

							<!-- Recherche simple (mode non-db) -->
							<b-input-group v-show="!db" size="sm" style="max-width:300px; flex:1 1 auto">
								<b-input-group-prepend is-text><i class="fa fa-search"></i></b-input-group-prepend>
								<b-form-input v-model="search" placeholder="Rechercher..." @keyup.enter="search_documents()"></b-form-input>
								<b-input-group-append>
									<b-button variant="primary" size="sm" @click="search_documents()">Rechercher</b-button>
								</b-input-group-append>
							</b-input-group>

							<!-- Bouton recherche avancée (mode db) -->
							<b-button
								v-show="db"
								size="sm"
								variant="outline-info"
								class="flex-shrink-0"
								v-b-toggle="'search-' + id">
								<i class="fa fa-filter mr-1"></i>Recherche
							</b-button>

							<div class="border-left align-self-stretch mx-1 flex-shrink-0"></div>

							<!-- Bouton créer -->
							<b-button
								v-if="rules.create && !share"
								variant="success"
								v-b-toggle="'add-' + id">
								<i class="fas fa-plus mr-1"></i>Ajouter
							</b-button>

							<template v-if="!select && !minimal && tab_idx === 0">
								<div class="border-left align-self-stretch mx-1"></div>
								<b-button-group size="sm">
									<b-button
										variant="outline-secondary"
										v-b-modal="'zip-' + id"
										v-b-tooltip.hover title="Télécharger en ZIP (sélection ou résultats)">
										<i class="fas fa-file-archive mr-1"></i>Zip
									</b-button>
									<b-button
										v-if="rules.delete && !share_rules"
										variant="outline-danger"
										v-b-modal="'delete-' + id"
										v-b-tooltip.hover title="Supprimer (sélection ou résultats)">
										<i class="fas fa-trash mr-1"></i>Supprimer
									</b-button>
									<b-button
										v-if="rules.update && !share_rules"
										variant="outline-secondary"
										v-b-tooltip.hover title="Déplacer la sélection"
										@click="move_document(null)">
										<i class="fas fa-arrows-alt mr-1"></i>Déplacer
									</b-button>
								</b-button-group>
								<b-button-group size="sm" v-if="db && document_tags_actions">
									<b-button
										:variant="toggle_add_multi_tags ? 'success' : 'outline-success'"
										v-b-tooltip.hover title="Ajouter des tags à la sélection"
										@click="toggle_multi_tags({add: true, remove: false})">
										<i class="fas fa-tag mr-1"></i>+ Tags
									</b-button>
									<b-button
										:variant="toggle_remove_multi_tags ? 'danger' : 'outline-danger'"
										v-b-tooltip.hover title="Supprimer des tags de la sélection"
										@click="toggle_multi_tags({add: false, remove: true})">
										<i class="fas fa-tag mr-1"></i>- Tags
									</b-button>
								</b-button-group>
							</template>

							<!-- Boutons config (toujours à droite) -->
							<template v-if="!minimal">
								<div class="border-left align-self-stretch mx-1"></div>
								<!-- Toggle vue tags / dossiers -->
								<b-button
									v-if="db && !rules.root_folder && !document_tags"
									size="sm"
									variant="outline-info"
									v-b-tooltip.hover title="Vue par tags"
									@click="document_tags = true; get_folder(false, false, true)">
									<i class="fas fa-tags"></i>
								</b-button>
								<b-button
									v-if="db && !rules.root_folder && document_tags"
									size="sm"
									variant="info"
									v-b-tooltip.hover title="Vue par dossiers"
									@click="document_tags = false; get_folder(false, false, true)">
									<i class="fas fa-folder"></i>
								</b-button>
								<b-dropdown
									v-if="db && (columns_ged.length > 0 || main_tags_col)"
									size="sm"
									variant="outline-info"
									no-caret right
									v-b-tooltip.hover title="Visibilité des colonnes">
									<template #button-content><i class="fas fa-columns"></i></template>
									<b-dropdown-header>Colonnes visibles</b-dropdown-header>
									<b-dropdown-form style="min-width:220px">
										<b-form-checkbox
											v-if="main_tags_col"
											v-model="tags_col_visible"
											class="mb-1">Tags</b-form-checkbox>
										<b-form-checkbox
											v-for="col in columns_ged.filter(c => c.display !== false)"
											:key="col.column"
											v-model="col_visibility[col.column]"
											class="mb-1">{{ col.label }}</b-form-checkbox>
									</b-dropdown-form>
								</b-dropdown>
								<b-button
									v-if="db && can_config"
									size="sm"
									variant="outline-secondary"
									v-b-tooltip.hover title="Configurer les colonnes GED"
									@click="open_config()">
									<i class="fas fa-cog"></i>
								</b-button>
								<b-button
									v-if="db && is_admin"
									size="sm"
									variant="outline-warning"
									v-b-tooltip.hover title="Importer les documents Hermes dans la table"
									v-b-modal="'modal-transfer-hermes-' + id">
									<i class="fas fa-cloud-download-alt"></i>
								</b-button>
							</template>

							<!-- Compteur éléments -->
							<b-badge v-if="nb != null" variant="secondary" pill class="ml-2 px-3 py-2 flex-shrink-0" style="font-size:.85rem">
								{{ nb }} {{ (!minimal || view_files) ? 'élément(s)' : 'dossier(s)' }}
							</b-badge>
						</div>

					</div><!-- /.d-flex (toolbar) -->
				</b-card-body>
			</b-card>

			<!-- ── Panneau tags multi-sélection (db uniquement) ─────── -->
			<b-card v-if="db && document_tags_actions && (toggle_add_multi_tags || toggle_remove_multi_tags)" class="mb-3 shadow-sm" :class="toggle_add_multi_tags ? 'border-success' : 'border-danger'">
				<b-card-title class="h6 mb-3" :class="toggle_add_multi_tags ? 'text-success' : 'text-danger'">
					<i class="fas fa-tags mr-1"></i>
					{{ toggle_add_multi_tags ? 'Ajouter des tags à la sélection' : 'Supprimer des tags de la sélection' }}
				</b-card-title>
				<b-form-tags
					v-model="multi_add_tags"
					:placeholder="toggle_add_multi_tags ? 'Tapez Entrée pour ajouter un tag' : 'Tapez Entrée pour ajouter un tag à supprimer'"
					:tag-variant="toggle_add_multi_tags ? 'success' : 'danger'"
					size="sm"
					add-on-change
					no-outer-focus>
				</b-form-tags>
				<div class="mt-3 d-flex" style="gap:.5rem">
					<b-button
						:variant="toggle_add_multi_tags ? 'success' : 'danger'"
						size="sm"
						v-b-modal="'multi-tag-' + id">
						<i class="fas fa-check mr-1"></i>{{ toggle_add_multi_tags ? 'Appliquer l\'ajout' : 'Appliquer la suppression' }}
					</b-button>
					<b-button size="sm" variant="outline-secondary" @click="toggle_add_multi_tags = false; toggle_remove_multi_tags = false; multi_add_tags = []">
						Annuler
					</b-button>
				</div>
			</b-card>

			<!-- ── Panneau d'ajout (collapse) ───────────────────────── -->
			<b-collapse :id="'add-' + id" class="mb-3">
				<b-card class="shadow-sm border-success">
					<b-card-title class="h6 text-success mb-3">
						<i class="fas fa-plus-circle mr-1"></i>Ajouter des éléments
					</b-card-title>

					<b-row v-if="rules.create_folder">
						<b-col md="6">
							<label class="font-weight-semibold small text-muted text-uppercase mb-1">Nouveau dossier</label>
							<b-input-group size="sm">
								<b-form-input v-model="folder_input" placeholder="Nom du dossier" @keyup.enter="add_folder"></b-form-input>
								<b-input-group-append>
									<b-button variant="success" @click="add_folder">
										<i class="fas fa-folder-plus mr-1"></i>Créer
									</b-button>
								</b-input-group-append>
							</b-input-group>
						</b-col>
					</b-row>

					<hr v-if="rules.create_folder && (!minimal || view_files)" />

					<div v-if="!minimal || view_files">
						<label class="font-weight-semibold small text-muted text-uppercase mb-1">Importer des fichiers</label>

						<!-- Tags à l'import (db uniquement) -->
						<div v-if="db && document_tags_actions" class="mt-3">
							<!-- Colonne principale client.details.tags -->
							<div v-for="(tagCfg, colName) in tags" :key="'init-main-tag-' + colName" class="mb-2">
								<label class="small font-weight-bold mb-1">Tags</label>
								<b-form-tags
									v-model="init_tags[colName]"
									placeholder="Ajouter un tag"
									tag-variant="info"
									size="sm"
									no-outer-focus>
								</b-form-tags>
							</div>
							<!-- Colonnes columns_ged type:'tags' -->
							<div v-for="col in columns_ged.filter(c => c.type === 'tags' && c.editable !== false)" :key="'init-tag-' + col.key" class="mb-2">
								<label class="small font-weight-bold mb-1">{{ col.label }}</label>
								<b-form-tags
									v-model="init_tags[col.column]"
									:placeholder="'Ajouter ' + col.label"
									tag-variant="info"
									size="sm"
									no-outer-focus>
								</b-form-tags>
							</div>
						</div>
						<!-- Colonnes de type 'column' à l'import (db uniquement) -->
						<div v-if="db" class="mt-3">
							<div v-for="col in columns_ged.filter(c => c.type === 'column' && c.editable !== false)" :key="'init-col-' + col.key" class="mb-2">
								<label class="small font-weight-bold mb-1">{{ col.label }}</label>
								<b-form-input
									v-model="init_columns[col.column]"
									:placeholder="col.label"
									size="sm">
								</b-form-input>
							</div>
						</div>

						<div class="d-flex align-items-center mt-2" style="gap:1rem">
							<b-form-checkbox v-model="public_file" value="true" unchecked-value="false" class="mb-0">
								<small>Fichiers publics</small>
							</b-form-checkbox>
						</div>

						<vue-dropzone
							v-on:vdropzone-thumbnail="thumbnail"
							v-on:vdropzone-sending="sendingEvent"
							v-on:vdropzone-queue-complete="queueComplete"
							v-on:vdropzone-file-added="add_file_to_queue"
							v-on:vdropzone-total-upload-progress="progress"
							ref="myVueDropzone"
							id="dropzone"
							:options="dropzoneOptions">
						</vue-dropzone>

						<b-progress v-if="launch" :value="counter" animated variant="success" class="mt-2" style="height:6px"></b-progress>

						<div v-if="nb_files > 0" class="mt-3">
							<div class="table-responsive">
								<table class="table table-sm table-striped mb-2">
									<thead>
										<tr>
											<th>Nom</th>
											<th>Taille</th>
											<th v-for="(tagCfg, colName) in tags" v-if="tags_editable" :key="'th-main-' + colName">Tags</th>
											<th v-for="col in columns_ged.filter(c => c.type === 'tags' && c.editable !== false)" :key="'th-ctag-' + col.key">{{ col.label }}</th>
											<th v-for="col in columns_ged.filter(c => c.type === 'column' && c.editable !== false)" :key="'th-ccol-' + col.key">{{ col.label }}</th>
											<th></th>
										</tr>
									</thead>
									<tbody>
										<tr v-for="f in files" :key="f.del">
											<td>{{ f.name }}</td>
											<td class="text-nowrap">{{ f.size }}</td>
											<td v-for="(tagCfg, colName) in tags" v-if="tags_editable" :key="'td-main-' + colName" style="min-width:160px">
												<b-form-tags
													v-if="file_specific_data[f.del]"
													v-model="file_specific_data[f.del].tags[colName]"
													size="sm" tag-variant="info"
													placeholder="Ajouter un tag"
													no-outer-focus>
												</b-form-tags>
											</td>
											<td v-for="col in columns_ged.filter(c => c.type === 'tags' && c.editable !== false)" :key="'td-ctag-' + col.key" style="min-width:160px">
												<b-form-tags
													v-if="file_specific_data[f.del]"
													v-model="file_specific_data[f.del].tags[col.column]"
													size="sm" tag-variant="info"
													:placeholder="'Ajouter ' + col.label"
													no-outer-focus>
												</b-form-tags>
											</td>
											<td v-for="col in columns_ged.filter(c => c.type === 'column' && c.editable !== false)" :key="'td-ccol-' + col.key" style="min-width:140px">
												<b-form-input
													v-if="file_specific_data[f.del]"
													v-model="file_specific_data[f.del].columns[col.column]"
													size="sm" :placeholder="col.label">
												</b-form-input>
											</td>
											<td>
												<b-button size="sm" variant="outline-danger" @click="del_file(f.del)">
													<i class="fas fa-times"></i>
												</b-button>
											</td>
										</tr>
									</tbody>
								</table>
							</div>
							<b-button variant="success" size="sm" @click="start">
								<i class="fas fa-upload mr-1"></i>Envoyer {{ nb_files }} fichier(s)
							</b-button>
						</div>
					</div>
				</b-card>
			</b-collapse>

			<!-- ── Recherche avancée / query builder (mode db) ──────── -->
			<b-collapse :id="'search-' + id" class="mb-3">
				<b-card class="shadow-sm border-info">
					<b-card-title class="h6 text-info mb-3">
						<i class="fas fa-sliders-h mr-1"></i>Recherche
					</b-card-title>
					<div :id="'builder-' + id" class="mb-3"></div>
					<b-row class="align-items-end">
						<b-col sm="3">
							<label class="small text-muted">Résultats par page</label>
							<b-form-input type="number" min="1" size="sm" v-model="limit"></b-form-input>
						</b-col>
						<b-col sm="3">
							<label class="small text-muted">Trié par</label>
							<b-form-select size="sm" v-model="order" :options="order_options"></b-form-select>
						</b-col>
						<b-col sm="2">
							<label class="small text-muted">Extension</label>
							<b-form-input size="sm" v-model="extension" placeholder="pdf, jpg…"></b-form-input>
						</b-col>
						<b-col sm="2" class="d-flex align-items-end pb-1">
							<b-form-checkbox v-model="deep" class="small">Sous-dossiers</b-form-checkbox>
						</b-col>
						<b-col sm="2" class="d-flex align-items-end">
							<b-button-group size="sm" class="w-100">
								<b-button variant="info" @click="search_documents()">
									<i class="fa fa-search mr-1"></i>Rechercher
								</b-button>
								<b-button variant="outline-secondary" @click="reset_builder()" v-b-tooltip.hover title="Réinitialiser">
									<i class="fas fa-undo"></i>
								</b-button>
							</b-button-group>
						</b-col>
					</b-row>
				</b-card>
			</b-collapse>

			<!-- ── Pagination supérieure ─────────────────────────────── -->
			<div v-if="show_files === null && (page > 1 || documents.length == limit)" class="d-flex align-items-center justify-content-between mb-2">
				<small class="text-muted">Page {{ page }}</small>
				<b-pagination
					v-model="page"
					:total-rows="nb || (page * limit)"
					:per-page="limit"
					size="sm"
					class="mb-0"
					@change="p => { page = p; set_filters(); search_documents(); }">
				</b-pagination>
			</div>

			<!-- ── Onglets : Liste / Galerie / Plein écran ──────────── -->
			<b-tabs v-model="tab_idx" content-class="mt-0" nav-class="border-bottom">

				<!-- ── Vue Liste ───────────────────────────────────── -->
				<b-tab @click="show_files = null">
					<template #title><i class="fas fa-list mr-1"></i>Liste</template>

					<b-table
						striped
						hover
						responsive
						class="mb-0 ged-table"
						:items="documents"
						:no-sort-reset="true"
						:no-local-sorting="true"
						:fields="fields_with_extra"
						primary-key="uid">

						<!-- Icône / aperçu -->
						<template v-slot:cell(type)="row">
							<div class="text-center" style="font-size:1.4em">
								<span v-if="row.item.type == 'folder'" class="text-warning">
									<i class="fas fa-folder"></i>
								</span>
								<b-button v-else size="sm" variant="link" class="p-0" @click="view_document(row.item)">
									<span v-if="row.item.thumbnail" style="display:block;line-height:0;width:48px;height:48px;overflow:hidden;border-radius:4px">
										<viewFile :showFileName="false" style="width:48px;height:48px" name="image.png" :data="row.item.thumbnail"></viewFile>
									</span>
									<i v-else :class="file_icon_class(row.item.libelle) + ' fa-lg'"></i>
								</b-button>
							</div>
						</template>

						<!-- Nom -->
						<template v-slot:cell(libelle)="row">
							<b-button v-if="row.item.type == 'folder'" variant="link" class="p-0 text-dark font-weight-semibold" @click="change_folder(row.item)">
								<i class="fas fa-folder text-warning mr-1"></i>{{ row.item.libelle.replace(/ +--.*/, '').trim() || 'dossier sans nom' }}
							</b-button>
							<b-button v-else v-b-tooltip.hover :title="row.item.url" variant="link" class="p-0 text-dark" @click="dowload(row.item)">
								{{ row.item.libelle.trim() || 'fichier sans nom' }}
							</b-button>
						</template>

						<!-- Date -->
						<template v-slot:cell(dateheure)="row">
							<small class="text-muted">{{ [row.item.dateheure] | moment('DD/MM/YYYY HH:mm') }}</small>
						</template>

						<!-- Commentaires -->
						<template v-slot:cell(comments)="row">
							<b-button
								v-if="row.item[column_comments] && row.item[column_comments].length > 0"
								size="sm" variant="outline-secondary"
								@click="comments = row.item[column_comments]"
								v-b-modal="'modal-comments-' + id">
								<i class="far fa-comment mr-1"></i>{{ row.item[column_comments].length }}
							</b-button>
						</template>

						<!-- Checkbox header -->
						<template v-slot:head(checkbox)="">
							<b-form-checkbox @change="f_select" v-model="select_all" size="sm"></b-form-checkbox>
						</template>

						<!-- Checkbox -->
						<template v-slot:cell(checkbox)="row">
							<b-form-checkbox v-model="row.item.checkbox" size="sm"></b-form-checkbox>
						</template>

<!-- Colonnes de type tags (tags principaux + columns_ged type:'tags') -->
				<template v-for="column in tags_columns" v-slot:[`cell(${column})`]="row">
					<div :key="column">
						<!-- columns_ged type:'tags' : accès direct via item[column] -->
						<template v-if="columns_ged.some(c => c.column === column && c.type === 'tags')">
							<span
								v-for="(tagVal, tidx) in gedTagsArray(row.item, column)"
								:key="tidx"
								:style="'background-color:' + generateColorRgb(tagVal) + '; color:#fff; margin-right:3px; margin-bottom:2px; display:inline-flex; align-items:center; cursor:' + (rules.update && !share_rules && columns_ged.some(c => c.column === column && c.editable !== false) ? 'pointer' : 'default')"
								class="badge"
								@click.stop="rules.update && !share_rules && columns_ged.some(c => c.column === column && c.editable !== false) ? (active_tag = (active_tag && active_tag.uid === row.item.uid && active_tag.idx === (row.item.uid+'-'+column+'-'+tidx) ? null : { uid: row.item.uid, idx: row.item.uid+'-'+column+'-'+tidx })) : null">
								{{ tagVal }}
								<button
									v-if="active_tag && active_tag.uid === row.item.uid && active_tag.idx === (row.item.uid+'-'+column+'-'+tidx) && columns_ged.some(c => c.column === column && c.editable !== false)"
									type="button" class="close ml-1"
									style="font-size:.65rem;color:#fff;opacity:1;text-shadow:none;line-height:1"
									@click.stop="ged_remove_tag(row.item, column, tidx); active_tag = null">
									&times;
								</button>
							</span>
							<!-- Bouton + pour ajouter un tag -->
							<span v-if="rules.update && !share_rules && row.item.type != 'folder' && columns_ged.some(c => c.column === column && c.editable !== false)"
								:class="['ged-tag-add-btn', { 'ged-tag-add-btn--active': ged_tag_open && ged_tag_open.uid === row.item.uid && ged_tag_open.col === column }]"
								@click.stop="ged_tag_open = (ged_tag_open && ged_tag_open.uid === row.item.uid && ged_tag_open.col === column) ? null : { uid: row.item.uid, col: column, val: '' }">
								<i class="fas fa-plus"></i>
							</span>
							<!-- Formulaire inline ajout tag ged -->
							<div v-if="ged_tag_open && ged_tag_open.uid === row.item.uid && ged_tag_open.col === column" class="mt-1" @click.stop>
								<b-input-group size="sm">
									<b-form-input v-model="ged_tag_open.val" placeholder="Nouveau tag" autofocus @keyup.enter="ged_add_tag_from_open(row.item)" @keyup.esc="ged_tag_open = null"></b-form-input>
									<b-input-group-append>
										<b-button variant="success" @click="ged_add_tag_from_open(row.item)"><i class="fas fa-plus"></i></b-button>
									</b-input-group-append>
								</b-input-group>
							</div>
						</template>
						<!-- Tags principaux : accès via row.item.tags (jointure) -->
						<template v-else>
							<span v-if="row.item.tags">
								<span v-for="(tag, tidx) in row.item.tags.filter(t => t.type === column)" :key="tidx"
									:style="'background-color:' + generateColorRgb(tag.name) + '; color:#fff; margin-right:3px; margin-bottom:2px; display:inline-flex; align-items:center; cursor:' + (db && document_tags_actions ? 'pointer' : 'default')"
									class="badge"
									@click.stop="db && document_tags_actions ? active_tag = (active_tag && active_tag.uid === row.item.uid && active_tag.idx === (row.item.uid+'-'+tidx) ? null : { uid: row.item.uid, idx: row.item.uid+'-'+tidx }) : null">
									{{ tag.name }}
									<button
										v-if="active_tag && active_tag.uid === row.item.uid && active_tag.idx === (row.item.uid+'-'+tidx) && tags_editable"
										type="button"
										class="close ml-1"
										style="font-size:.65rem; color:#fff; opacity:1; text-shadow:none; line-height:1"
										@click.stop="remove_tag(row.item, tag); active_tag = null">
										&times;
									</button>
								</span>
							</span>
							<!-- Bouton + pour ajouter un tag principal -->
							<span v-if="db && document_tags_actions && tags_editable && row.item.type != 'folder'"
								:class="['ged-tag-add-btn', { 'ged-tag-add-btn--active': row.item.add_tag }]"
								@click.stop="add_tag_button(row.item, !row.item.add_tag, column)">
								<i :class="row.item.add_tag ? 'fas fa-times' : 'fas fa-plus'"></i>
							</span>
							<!-- Formulaire inline ajout tag principal -->
							<div v-if="db && row.item.add_tag && tags_editable" class="mt-1" @click.stop>
								<b-input-group size="sm">
									<b-form-input v-model="tag.name" placeholder="Nouveau tag" autofocus @keyup.enter="add_tag(row.item, column)" @keyup.esc="add_tag_button(row.item, false, column)"></b-form-input>
									<b-input-group-append>
										<b-button variant="success" @click="add_tag(row.item, column)"><i class="fas fa-plus"></i></b-button>
									</b-input-group-append>
								</b-input-group>
							</div>
						</template>
					</div>
				</template>

				<!-- Colonnes de type "column" (valeur brute, éditable si droits) -->
				<template v-for="col in columns_ged.filter(c => c.type === 'column')" v-slot:[`cell(${col.column})`]="row">
					<div :key="col.column">
						<!-- Mode édition -->
						<b-form-input
							v-if="ged_cell_edit.uid === row.item.uid && ged_cell_edit.col === col.column"
							v-model="ged_cell_edit.val"
							size="sm"
							autofocus
							@blur="save_ged_col(row.item, col.column)"
							@keyup.enter="save_ged_col(row.item, col.column)"
							@keyup.esc="ged_cell_edit = { uid: null, col: null, val: '' }"
						></b-form-input>
						<!-- Mode lecture -->
						<span
							v-else
							:class="rules.update && !share_rules && col.editable !== false ? 'ged-editable-cell' : ''"
							@click="rules.update && !share_rules && col.editable !== false ? (ged_cell_edit = { uid: row.item.uid, col: col.column, val: row.item[col.column] || '' }) : null"
						>{{ row.item[col.column] || '' }}<i v-if="rules.update && !share_rules && col.editable !== false" class="fas fa-pencil-alt ml-1 text-muted ged-pencil" style="font-size:.7rem"></i></span>
					</div>
				</template>

						<!-- Actions -->
						<template v-slot:cell(uid)="row">
							<b-button-group size="sm">
								<!-- Voir -->
								<b-button v-if="row.item.type != 'folder'" :variant="canPreview(row.item.libelle) ? 'outline-primary' : 'outline-secondary'" v-b-tooltip.hover :title="canPreview(row.item.libelle) ? 'Visualiser' : 'Télécharger'" @click="view_document(row.item)">
									<i :class="canPreview(row.item.libelle) ? 'fas fa-eye' : 'fas fa-download'"></i>
								</b-button>
								<!-- Visibilité public/privé -->
								<template v-if="row.item.type != 'folder' && !minimal">
									<b-button v-if="rules.update && !share_rules && !select" variant="outline-secondary" v-b-tooltip.hover :title="row.item.public ? 'Passer en privé' : 'Passer en public'" @click="update_public(row.item, !row.item.public)">
										<i :class="row.item.public ? 'fas fa-lock-open text-success' : 'fas fa-lock text-warning'"></i>
									</b-button>
									<b-button v-else variant="link" disabled class="px-1">
										<i :class="row.item.public ? 'fas fa-lock-open text-success' : 'fas fa-lock text-warning'"></i>
									</b-button>
								</template>
								<!-- Déplacer -->
								<b-button v-if="!minimal && rules.update && !share_rules && !select && !share" variant="outline-secondary" v-b-tooltip.hover title="Renommer / déplacer" @click="move_document(row.item)">
									<i class="fas fa-pen"></i>
								</b-button>
								<!-- Ajouter/retirer de la sélection -->
								<b-button v-if="select && row.item.type != 'folder' && !data_files_uid.includes(row.item.uid)" variant="outline-success" v-b-tooltip.hover title="Sélectionner" @click="add_selected(row.item)">
									<i class="fas fa-plus"></i>
								</b-button>
								<b-button v-if="select && row.item.type != 'folder' && data_files_uid.includes(row.item.uid)" variant="success" v-b-tooltip.hover title="Retirer" @click="del_data_files(row.item.id)">
									<i class="fas fa-check"></i>
								</b-button>
								<!-- Supprimer -->
								<b-button v-if="!minimal && rules.delete && !share_rules && !select" variant="outline-danger" v-b-tooltip.hover title="Supprimer" @click="delete_document(row.item)">
									<i class="fas fa-trash"></i>
								</b-button>
							</b-button-group>
						</template>
					</b-table>

					<!-- Modals actions groupées -->
					<b-modal ref="zip" :hide-footer="true" size="md" :id="'zip-' + id" title="Télécharger en ZIP">
						<div class="list-group list-group-flush">
							<b-button variant="outline-secondary" class="list-group-item list-group-item-action" @click="dowloadZip('selection')">
								<i class="fas fa-check-square mr-2 text-primary"></i>
								Sélection ({{ documents.filter(d => d.checkbox).length }} élément(s))
							</b-button>
							<b-button variant="outline-secondary" class="list-group-item list-group-item-action" @click="dowloadZip('search')">
								<i class="fas fa-search mr-2 text-info"></i>
								Tous les résultats ({{ nb }} éléments)
							</b-button>
						</div>
					</b-modal>

					<!-- Modal bloquante de progression de génération du ZIP -->
					<b-modal
						:id="'zip-progress-' + id"
						v-model="zip_progress_visible"
						hide-header-close
						hide-footer
						no-close-on-backdrop
						no-close-on-esc
						centered
						title="Génération du ZIP en cours">
						<div class="text-center py-3">
							<template v-if="zip_progress.step === 'downloading'">
								<p class="mb-2">
									Téléchargement des fichiers
									<strong>({{ zip_progress.current }} / {{ zip_progress.total || '?' }})</strong>
								</p>
								<b-progress :max="zip_progress.total || 1" animated variant="info" height="1.2rem">
									<b-progress-bar :value="zip_progress.current" :label="zip_progress.total ? Math.round(zip_progress.current / zip_progress.total * 100) + ' %' : ''"></b-progress-bar>
								</b-progress>
							</template>
							<template v-else-if="zip_progress.step === 'zipping'">
								<p class="mb-0"><b-spinner small class="mr-2"></b-spinner>Génération de l'archive ZIP en cours...</p>
							</template>
							<template v-else-if="zip_progress.step === 'sending'">
								<p class="mb-2">
									<b-spinner small class="mr-2"></b-spinner>Téléchargement du fichier ZIP...
									<span v-if="zip_progress.bytes"> ({{ (zip_progress.bytes / 1024 / 1024).toFixed(1) }} Mo{{ zip_progress.total_bytes ? ' / ' + (zip_progress.total_bytes / 1024 / 1024).toFixed(1) + ' Mo' : '' }})</span>
								</p>
								<b-progress v-if="zip_progress.total_bytes" :value="zip_progress.bytes" :max="zip_progress.total_bytes" animated variant="success" height="1.2rem"></b-progress>
							</template>
							<template v-else-if="zip_progress.step === 'error'">
								<p class="text-danger mb-0"><i class="fas fa-exclamation-triangle mr-1"></i>Une erreur est survenue pendant la génération du ZIP.</p>
							</template>
							<template v-else>
								<p class="mb-0"><b-spinner small class="mr-2"></b-spinner>Préparation...</p>
							</template>
							<p class="text-muted small mt-3 mb-0">Merci de ne pas fermer cette fenêtre.</p>
						</div>
					</b-modal>

					<b-modal ref="delete" :hide-footer="true" size="md" :id="'delete-' + id" title="Confirmer la suppression">
						<b-alert show variant="warning" class="mb-3">
							<i class="fas fa-exclamation-triangle mr-1"></i>
							Cette action est irréversible.
						</b-alert>
						<div class="list-group list-group-flush">
							<b-button variant="outline-danger" class="list-group-item list-group-item-action" @click="deleteFiles('selection')">
								Supprimer la sélection ({{ documents.filter(d => d.checkbox).length }} élément(s))
							</b-button>
							<b-button variant="outline-danger" class="list-group-item list-group-item-action" @click="deleteFiles('search')">
								Supprimer tous les résultats ({{ nb }} éléments)
							</b-button>
						</div>
					</b-modal>

					<!-- Modal confirmation tags multi-sélection -->
					<b-modal ref="multi-tag" :hide-footer="true" size="md" :id="'multi-tag-' + id"
						:title="toggle_add_multi_tags ? 'Ajouter des tags' : 'Supprimer des tags'">
						<div class="list-group list-group-flush">
							<b-button
								:variant="toggle_add_multi_tags ? 'outline-success' : 'outline-danger'"
								class="list-group-item list-group-item-action"
								@click="add_tags_to_selection('selection')">
								{{ toggle_add_multi_tags ? 'Ajouter' : 'Supprimer' }} dans la sélection ({{ documents.filter(d => d.checkbox).length }} élément(s))
							</b-button>
							<b-button
								:variant="toggle_add_multi_tags ? 'outline-success' : 'outline-danger'"
								class="list-group-item list-group-item-action"
								@click="add_tags_to_selection('search')">
								{{ toggle_add_multi_tags ? 'Ajouter' : 'Supprimer' }} dans tous les résultats ({{ nb }} éléments)
							</b-button>
						</div>
					</b-modal>
				</b-tab>
				<b-tab @click="show_files = null">
					<template #title><i class="fas fa-th-large mr-1"></i>Galerie</template>
					<div class="ged-gallery mt-3">
						<b-card
							v-for="doc in documents"
							:key="doc.uid"
							class="ged-gallery-card shadow-sm"
							:class="{ 'border-warning': doc.type == 'folder', 'border-primary': data_files_uid.includes(doc.uid) }"
							no-body>

							<!-- Zone aperçu -->
							<div class="ged-gallery-preview d-flex align-items-center justify-content-center bg-light"
								style="height:120px; cursor:pointer; overflow:hidden; border-radius: 4px 4px 0 0"
								@click="doc.type == 'folder' ? change_folder(doc) : view_document(doc)">
								<span v-if="doc.type == 'folder'" style="font-size:3rem" class="text-warning">
									<i class="fas fa-folder"></i>
								</span>
								<template v-else>
									<viewFile v-if="doc.thumbnail" :showFileName="false" style="width:100%;height:100%;object-fit:cover" name="image.png" :data="doc.thumbnail"></viewFile>
									<i v-else :class="file_icon_class(doc.libelle)" style="font-size:3rem; opacity:.6"></i>
								</template>
							</div>

							<!-- Corps de la carte -->
							<b-card-body class="p-2">
								<div class="mb-1">
									<b-button
										variant="link"
										class="p-0 text-dark font-weight-semibold text-truncate"
										style="max-width:100%; font-size:.85rem; text-align:left; white-space:normal; word-break:break-word"
										v-b-tooltip.hover :title="doc.type != 'folder' ? doc.url : null"
										@click="doc.type == 'folder' ? change_folder(doc) : view_document(doc)">
										{{ (doc.type == 'folder' ? doc.libelle.replace(/ +--.*/, '') : doc.libelle).trim() || (doc.type == 'folder' ? 'dossier sans nom' : 'fichier sans nom') }}
									</b-button>
								</div>

								<small class="text-muted d-block mb-2">
									<i class="far fa-clock mr-1"></i>{{ [doc.dateheure] | moment('DD/MM/YYYY') }}
								</small>

								<!-- Tags du document -->
								<div v-if="doc.tags && doc.tags.length" class="mb-1">
									<span
										v-for="(tag, idx) in doc.tags" :key="idx"
										:style="'background-color:' + generateColorRgb(tag.name) + '; color:#fff; margin-right:3px; margin-bottom:3px; display:inline-flex; align-items:center; cursor:' + (db && document_tags_actions ? 'pointer' : 'default')"
										class="badge"
										@click.stop="db && document_tags_actions ? active_tag = (active_tag && active_tag.uid === doc.uid && active_tag.idx === idx ? null : { uid: doc.uid, idx }) : null">
										{{ tag.name }}
										<button
											v-if="active_tag && active_tag.uid === doc.uid && active_tag.idx === idx"
											type="button"
											class="close ml-1"
											style="font-size:.65rem; color:#fff; opacity:1; text-shadow:none; line-height:1"
											@click.stop="remove_tag(doc, tag); active_tag = null">
											&times;
										</button>
									</span>
								</div>

								<!-- Boutons action galerie -->
								<div class="d-flex justify-content-between align-items-center mt-1" style="gap:.25rem">
									<b-button-group size="sm">
										<b-button v-if="doc.type != 'folder'" :variant="canPreview(doc.libelle) ? 'outline-primary' : 'outline-secondary'" @click="view_document(doc)" v-b-tooltip.hover :title="canPreview(doc.libelle) ? 'Visualiser' : 'Télécharger'">
											<i :class="canPreview(doc.libelle) ? 'fas fa-eye' : 'fas fa-download'"></i>
										</b-button>
									</b-button-group>

									<b-button size="sm" variant="outline-info" @click="detail_doc = doc; $bvModal.show('modal-doc-detail-' + id)" v-b-tooltip.hover title="Détails / Actions">
										<i class="fas fa-info-circle"></i>
									</b-button>
								</div>
							</b-card-body>
						</b-card>
					</div>
				</b-tab>

				<!-- ── Vue Plein écran ─────────────────────────────── -->
				<b-tab @click="show_files = false" v-if="!minimal && nb != null">
					<template #title><i class="fas fa-expand mr-1"></i>Plein écran</template>
					<div v-if="show_files && show_files_idx < nb" class="text-center py-3">
						<!-- Navigation -->
						<div class="d-flex align-items-center justify-content-center mb-3" style="gap:1rem">
							<b-button variant="outline-secondary" :disabled="show_files_idx <= 0" @click="show_files = false; show_files_idx--">
								<i class="fas fa-chevron-left"></i>
							</b-button>
							<b-badge variant="secondary" class="px-3 py-2" style="font-size:.9rem">
								{{ show_files_idx + 1 }} / {{ nb }}
							</b-badge>
							<b-button variant="outline-secondary" :disabled="show_files_idx + 1 >= nb" @click="show_files = false; show_files_idx++">
								<i class="fas fa-chevron-right"></i>
							</b-button>
						</div>
						<!-- Infos fichier + bouton détail -->
						<div class="d-flex align-items-center justify-content-center flex-wrap mb-2" style="gap:.5rem">
							<strong v-b-tooltip.hover :title="current_document.url">{{ current_document.libelle }}</strong>
							<small class="text-muted">
								<i class="far fa-clock mr-1"></i>{{ [current_document.dateheure] | moment('LLL') }}
							</small>
							<b-badge :variant="current_document.public ? 'success' : 'secondary'" pill>
								<i :class="current_document.public ? 'fas fa-lock-open mr-1' : 'fas fa-lock mr-1'"></i>
								{{ current_document.public ? 'Public' : 'Privé' }}
							</b-badge>
							<b-button size="sm" variant="outline-secondary" @click="dowload(current_document)" v-b-tooltip.hover title="Télécharger">
								<i class="fas fa-download mr-1"></i>Télécharger
							</b-button>
							<b-button size="sm" variant="outline-info" @click="detail_doc = current_document; $bvModal.show('modal-doc-detail-' + id)" v-b-tooltip.hover title="Détails / Actions">
								<i class="fas fa-info-circle mr-1"></i>Détails
							</b-button>
						</div>
						<!-- Tags principaux (lecture seule — gestion via Détails) -->
						<div class="mb-3 d-flex flex-wrap align-items-center justify-content-center" style="gap:4px">
							<span
								v-for="(tag, idx) in (current_document.tags || [])" :key="idx"
								:style="'background-color:' + generateColorRgb(tag.name) + '; color:#fff; display:inline-flex; align-items:center'"
								class="badge px-2 py-1">
								{{ tag.name }}
							</span>
						</div>
						<!-- Visualisation -->
						<div class="border rounded bg-light p-2" style="min-height:400px">
							<viewFile
								:showFileName="false"
								:showDownload="false"
								:uid_element="current_document.uid"
								:getDownload="false">
							</viewFile>
						</div>
					</div>
				</b-tab>
			</b-tabs>

			<!-- ── Pagination inférieure ─────────────────────────────── -->
			<div v-if="show_files === null && (page > 1 || documents.length == limit)" class="d-flex justify-content-center mt-3">
				<b-pagination
					v-model="page"
					:total-rows="nb || (page * limit)"
					:per-page="limit"
					size="sm"
					class="mb-0"
					@change="p => { page = p; set_filters(); search_documents(); }">
				</b-pagination>
			</div>
		</b-collapse>

		<!-- ── Zone de sélection (mode select) ──────────────────────── -->
		<b-card v-if="select && select_view" no-body class="mt-3 shadow-sm">
			<b-card-header class="py-2 px-3 bg-light">
				<strong class="small text-uppercase text-muted">
					<i class="fas fa-check-square mr-1"></i>Sélection
					<b-badge variant="primary" pill class="ml-1">{{ data_files_desc.length }}</b-badge>
				</strong>
			</b-card-header>
			<b-card-body class="p-0">
				<b-table v-if="download" small striped :items="data_files_desc"
					:fields="[{ key: 'libelle', label: 'Nom' }, { key: 'id', label: '', thStyle: { width: '50px' } }]"
					class="mb-0">
					<template v-slot:cell(id)="data">
						<b-button size="sm" variant="outline-danger" @click="del_data_files(data.item.id)">
							<i class="fas fa-times"></i>
						</b-button>
					</template>
				</b-table>
				<b-table v-else small striped responsive :items="data_files_desc"
					:fields="fields_doc" primary-key="uid" class="mb-0">
					<template v-slot:cell(type)="row">
						<div class="text-center" style="font-size:1.2em">
							<span v-if="row.item.type == 'folder'" class="text-warning"><i class="fas fa-folder"></i></span>
							<b-button v-else size="sm" variant="link" class="p-0" @click="view_document(row.item)">
								<i :class="file_icon_class(row.item.libelle)"></i>
							</b-button>
						</div>
					</template>
					<template v-slot:cell(uid)="row">
						<b-button-group size="sm">
							<b-button v-if="row.item.type != 'folder'" :variant="canPreview(row.item.libelle) ? 'outline-primary' : 'outline-secondary'" @click="view_document(row.item)">
								<i :class="canPreview(row.item.libelle) ? 'fas fa-eye' : 'fas fa-download'"></i>
							</b-button>
							<b-button variant="outline-danger" @click="del_data_files(row.item.id)">
								<i class="fas fa-times"></i>
							</b-button>
						</b-button-group>
					</template>
					<template v-slot:cell(dateheure)="row">
						<small class="text-muted">{{ [row.item.dateheure] | moment('DD/MM/YYYY') }}</small>
					</template>
					<template v-slot:cell(libelle)="row">
						<b-button v-if="row.item.type == 'folder'" variant="link" class="p-0 text-dark" @click="change_folder(row.item)">
							{{ row.item.libelle.replace(/ +--.*/, '').trim() || 'dossier sans nom' }}
						</b-button>
						<span v-else class="small">{{ row.item.libelle.trim() || 'fichier sans nom' }}</span>
					</template>
				</b-table>
			</b-card-body>
		</b-card>

		<!-- ── Modal commentaires ────────────────────────────────────── -->
		<b-modal size="lg" :id="'modal-comments-' + id" title="Commentaires" ok-only ok-title="Fermer">
			<div v-for="(comment, index) in comments" :key="index" class="mb-3">
				<div class="d-flex align-items-center mb-1">
					<strong class="mr-2">{{ comment.identity }}</strong>
					<small class="text-muted">{{ [comment.date] | moment('LLL') }}</small>
				</div>
				<p class="mb-0 text-muted" style="white-space:pre-wrap">{{ comment.comment }}</p>
				<hr v-if="index < comments.length - 1" class="my-2" />
			</div>
		</b-modal>

		<!-- ── Modal visualisation fichier ──────────────────────────── -->
		<b-modal :title="current_document ? current_document.libelle : ''" id="modal-document" size="xl">
			<viewFile
				:showFileName="false"
				:showDownload="false"
				ref="view_file"
				v-show="view_file"
				@download="hide">
			</viewFile>
			<template #modal-footer="{ ok }">
				<b-button size="sm" variant="outline-primary" @click="dowload(current_document)">
					<i class="fas fa-download mr-1"></i>Télécharger
				</b-button>
				<b-button size="sm" variant="secondary" @click="ok()">Fermer</b-button>
			</template>
		</b-modal>

		<!-- ── Modal détails document (galerie / plein écran) ─────────────────── -->
		<b-modal :id="'modal-doc-detail-' + id" :title="detail_doc ? detail_doc.libelle : ''" size="lg" scrollable>
			<template v-if="detail_doc">
				<!-- Date + public badge -->
				<div class="d-flex align-items-center mb-3" style="gap:.5rem">
					<small class="text-muted">
						<i class="far fa-clock mr-1"></i>{{ [detail_doc.dateheure] | moment('LLL') }}
					</small>
					<b-badge :variant="detail_doc.public ? 'success' : 'secondary'" pill>
						<i :class="detail_doc.public ? 'fas fa-lock-open mr-1' : 'fas fa-lock mr-1'"></i>
						{{ detail_doc.public ? 'Public' : 'Privé' }}
					</b-badge>
				</div>

				<!-- Tags principaux -->
				<div v-if="db && document_tags_actions" class="mb-3">
					<label class="font-weight-bold small text-uppercase text-muted mb-1">
						<i class="fas fa-tag mr-1"></i>Tags principaux
					</label>
					<div>
						<span
							v-for="(tag, tidx) in (detail_doc.tags || [])"
							:key="tidx"
							:style="'background-color:' + generateColorRgb(tag.name) + '; color:#fff; margin-right:4px; margin-bottom:4px; display:inline-flex; align-items:center; cursor:' + (tags_editable ? 'pointer' : 'default')"
							class="badge"
							@click.stop="tags_editable ? (active_tag = (active_tag && active_tag.uid === detail_doc.uid && active_tag.idx === (detail_doc.uid + '-modal-' + tidx) ? null : { uid: detail_doc.uid, idx: detail_doc.uid + '-modal-' + tidx })) : null">
							{{ tag.name }}
							<button
								v-if="tags_editable && active_tag && active_tag.uid === detail_doc.uid && active_tag.idx === (detail_doc.uid + '-modal-' + tidx)"
								type="button" class="close ml-1"
								style="font-size:.65rem;color:#fff;opacity:1;text-shadow:none;line-height:1"
								@click.stop="remove_tag(detail_doc, tag); active_tag = null">
								&times;
							</button>
						</span>
						<template v-if="tags_editable">
							<b-input-group v-if="detail_doc.add_tag" size="sm" class="mt-2" style="max-width:280px">
								<b-form-input v-model="tag.name" placeholder="Nouveau tag" @keyup.enter="add_tag(detail_doc, Object.keys(tags)[0])" @keyup.esc="add_tag_button(detail_doc, false, Object.keys(tags)[0])"></b-form-input>
								<b-input-group-append>
									<b-button variant="success" @click="add_tag(detail_doc, Object.keys(tags)[0])"><i class="fas fa-plus"></i></b-button>
								</b-input-group-append>
							</b-input-group>
							<b-button v-else size="sm" variant="outline-secondary" class="mt-1" @click="add_tag_button(detail_doc, true, Object.keys(tags)[0])">
								<i class="fas fa-plus mr-1"></i>Ajouter un tag
							</b-button>
						</template>
					</div>
				</div>

				<hr v-if="columns_ged.length" class="my-2">

				<!-- Colonnes GED type:'tags' -->
				<div v-for="col in columns_ged.filter(c => c.type === 'tags')" :key="'dtag-' + col.column" class="mb-3">
					<label class="font-weight-bold small text-uppercase text-muted mb-1">
						<i class="fas fa-tags mr-1"></i>{{ col.label || col.column }}
					</label>
					<div>
						<span
							v-for="(tagVal, tidx) in gedTagsArray(detail_doc, col.column)"
							:key="tidx"
							:style="'background-color:' + generateColorRgb(tagVal) + '; color:#fff; margin-right:4px; margin-bottom:4px; display:inline-flex; align-items:center; cursor:' + (rules.update && !share_rules && col.editable !== false ? 'pointer' : 'default')"
							class="badge"
							@click.stop="rules.update && !share_rules && col.editable !== false ? (active_tag = (active_tag && active_tag.uid === detail_doc.uid && active_tag.idx === (detail_doc.uid + '-modal-' + col.column + '-' + tidx) ? null : { uid: detail_doc.uid, idx: detail_doc.uid + '-modal-' + col.column + '-' + tidx })) : null">
							{{ tagVal }}
							<button
								v-if="rules.update && !share_rules && col.editable !== false && active_tag && active_tag.uid === detail_doc.uid && active_tag.idx === (detail_doc.uid + '-modal-' + col.column + '-' + tidx)"
								type="button" class="close ml-1"
								style="font-size:.65rem;color:#fff;opacity:1;text-shadow:none;line-height:1"
								@click.stop="ged_remove_tag(detail_doc, col.column, tidx); active_tag = null">
								&times;
							</button>
						</span>
						<template v-if="rules.update && !share_rules && col.editable !== false && detail_doc.type != 'folder'">
							<b-input-group v-if="ged_tag_open && ged_tag_open.uid === detail_doc.uid && ged_tag_open.col === col.column" size="sm" class="mt-2" style="max-width:280px">
								<b-form-input v-model="ged_tag_open.val" placeholder="Nouveau tag" autofocus @keyup.enter="ged_add_tag_from_open(detail_doc)" @keyup.esc="ged_tag_open = null"></b-form-input>
								<b-input-group-append>
									<b-button variant="success" @click="ged_add_tag_from_open(detail_doc)"><i class="fas fa-plus"></i></b-button>
								</b-input-group-append>
							</b-input-group>
							<b-button v-else size="sm" variant="outline-secondary" class="mt-1" @click="ged_tag_open = { uid: detail_doc.uid, col: col.column, val: '' }">
								<i class="fas fa-plus mr-1"></i>Ajouter
							</b-button>
						</template>
					</div>
				</div>

				<!-- Colonnes GED type:'column' -->
				<div v-for="col in columns_ged.filter(c => c.type === 'column')" :key="'dcol-' + col.column" class="mb-3">
					<label class="font-weight-bold small text-uppercase text-muted mb-1">{{ col.label || col.column }}</label>
					<b-form-input
						v-if="rules.update && !share_rules && col.editable !== false"
						:value="detail_doc[col.column] || ''"
						size="sm"
						@change="val => { $set(detail_doc, col.column, val); save_ged_col(detail_doc, col.column, val) }"
					></b-form-input>
					<div v-else class="border rounded px-2 py-1 bg-light small">{{ detail_doc[col.column] || '—' }}</div>
				</div>
			</template>

			<template #modal-footer="{ ok }">
				<!-- Public / privé -->
				<b-button
					v-if="detail_doc && detail_doc.type != 'folder' && !minimal && rules.update && !share_rules && !select"
					size="sm" :variant="detail_doc && detail_doc.public ? 'outline-success' : 'outline-secondary'"
					@click="update_public(detail_doc, !detail_doc.public)"
					v-b-tooltip.hover :title="detail_doc && detail_doc.public ? 'Passer en privé' : 'Passer en public'">
					<i :class="detail_doc && detail_doc.public ? 'fas fa-lock-open' : 'fas fa-lock'"></i>
				</b-button>
				<!-- Renommer/déplacer -->
				<b-button
					v-if="detail_doc && !minimal && rules.update && !share_rules && !select && !share"
					size="sm" variant="outline-secondary"
					@click="move_document(detail_doc); ok()"
					v-b-tooltip.hover title="Renommer / déplacer">
					<i class="fas fa-pen mr-1"></i>Renommer
				</b-button>
				<!-- Télécharger -->
				<b-button v-if="detail_doc" size="sm" variant="outline-primary" @click="dowload(detail_doc)" v-b-tooltip.hover title="Télécharger">
					<i class="fas fa-download mr-1"></i>Télécharger
				</b-button>
				<!-- Supprimer -->
				<b-button
					v-if="detail_doc && !minimal && rules.delete && !share_rules && !select"
					size="sm" variant="outline-danger"
					@click="delete_document(detail_doc); ok()"
					v-b-tooltip.hover title="Supprimer">
					<i class="fas fa-trash mr-1"></i>Supprimer
				</b-button>
				<b-button size="sm" variant="secondary" @click="ok()">Fermer</b-button>
			</template>
		</b-modal>

		<!-- ── Modal déplacer / renommer ────────────────────────────── -->
		<b-modal title="Déplacer / Renommer" id="modal-move" size="xl">
			<div v-if="current_document" class="mb-3">
				<label class="font-weight-semibold small">Nouveau nom</label>
				<b-form-input v-model="new_name" size="sm"></b-form-input>
			</div>
			<b-form-checkbox v-model="delete_if_empty" class="mb-3 small">
				Supprimer le dossier source s'il est vide après déplacement
			</b-form-checkbox>
			<label class="font-weight-semibold small text-muted text-uppercase mb-2">Dossier de destination</label>
			<div class="border rounded p-2">
				<ged ref="ged" :prop_path="folder_string == '/' ? null : folder_string" :minimal="true"></ged>
			</div>
			<template #modal-footer="{ ok }">
				<div v-if="current_document">
					<b-button size="sm" variant="success" @click="move(null); ok();">
						<i class="fas fa-check mr-1"></i>Valider
					</b-button>
				</div>
				<div v-else class="d-flex" style="gap:.5rem">
					<b-button size="sm" variant="success" @click="move('selection'); ok();">
						<i class="fas fa-check-square mr-1"></i>Déplacer la sélection ({{ documents.filter(d => d.checkbox).length }})
					</b-button>
					<b-button size="sm" variant="outline-success" @click="move('search'); ok();">
						<i class="fas fa-search mr-1"></i>Déplacer tous les résultats ({{ nb }})
					</b-button>
				</div>
			</template>
		</b-modal>

		<!-- ── Modale configuration colonnes GED ───────────────────────────────── -->
		<b-modal :id="'modal-config-ged-' + id" title="Configuration des colonnes GED" size="xl" scrollable>
			<!-- Colonne tags principale (client.details.tags) -->
			<h6 class="font-weight-bold mb-2"><i class="fas fa-tag mr-1 text-info"></i>Colonne tags principale <small class="text-muted font-weight-normal">(actions ajout/suppression, badges, filtre)</small></h6>
			<b-form-row class="mb-3">
				<b-col cols="4">
					<label class="small font-weight-bold">Colonne MySQL</label>
					<b-form-input v-model="config_tags_column" size="sm" placeholder="ex: tag"></b-form-input>
					<small class="text-muted">Nom exact de la colonne dans la table</small>
				</b-col>
				<b-col cols="4" class="d-flex align-items-end">
					<b-form-checkbox v-model="config_tags_editable" switch>
						<span class="small">Éditable par l'utilisateur</span>
					</b-form-checkbox>
				</b-col>
				<b-col cols="4" class="d-flex align-items-end">
					<b-form-checkbox v-model="config_tags_col_visible" switch>
						<span class="small">Visible dans le tableau</span>
					</b-form-checkbox>
				</b-col>
			</b-form-row>

			<hr class="my-3">
			<h6 class="font-weight-bold mb-2"><i class="fas fa-columns mr-1 text-info"></i>Colonnes supplémentaires</h6>

			<b-table-simple bordered small responsive class="mb-2">
				<b-thead head-variant="light">
					<b-tr>
						<b-th>Clé</b-th>
						<b-th>Colonne MySQL</b-th>
						<b-th>Label</b-th>
						<b-th>Affichage</b-th>
						<b-th>Filtrable</b-th>
						<b-th>Éditable</b-th>
						<b-th>Type</b-th>
						<b-th style="width:40px"></b-th>
					</b-tr>
				</b-thead>
				<b-tbody>
					<b-tr v-for="(col, idx) in config_columns_ged" :key="idx">
						<b-td>
							<b-form-input v-model="col.key" size="sm" placeholder="ex: tag"></b-form-input>
						</b-td>
						<b-td>
							<b-form-input v-model="col.column" size="sm" placeholder="ex: tag"></b-form-input>
						</b-td>
						<b-td>
							<b-form-input v-model="col.label" size="sm" placeholder="ex: Tags"></b-form-input>
						</b-td>
						<b-td>
							<b-form-select v-model="col.display" size="sm" :options="[
								{ value: 'table',  text: 'Visible (tableau)' },
								{ value: 'button', text: 'Masqué par défaut (menu)' },
								{ value: false,    text: 'Invisible' },
							]"></b-form-select>
						</b-td>
						<b-td class="text-center align-middle">
							<b-form-checkbox v-model="col.filterable" switch size="sm"></b-form-checkbox>
						</b-td>
						<b-td class="text-center align-middle">
							<b-form-checkbox v-model="col.editable" switch size="sm"></b-form-checkbox>
						</b-td>
						<b-td>
							<b-form-select v-model="col.type" size="sm" :options="[
								{ value: 'column', text: 'Colonne' },
								{ value: 'tags',   text: 'Tags (JSON)' },
							]"></b-form-select>
						</b-td>
						<b-td class="text-center align-middle">
							<b-button size="sm" variant="outline-danger" @click="config_remove_col(idx)">
								<i class="fas fa-times"></i>
							</b-button>
						</b-td>
					</b-tr>
					<b-tr v-if="config_columns_ged.length === 0">
						<b-td colspan="8" class="text-center text-muted py-3 small">Aucune colonne configurée</b-td>
					</b-tr>
				</b-tbody>
			</b-table-simple>

			<b-button size="sm" variant="outline-primary" @click="config_add_col()">
				<i class="fas fa-plus mr-1"></i>Ajouter une colonne
			</b-button>

			<template #modal-footer>
				<b-button variant="outline-secondary" @click="$bvModal.hide('modal-config-ged-' + id)">Annuler</b-button>
				<b-button variant="success" :disabled="config_saving" @click="save_columns_ged()">
					<i class="fas fa-save mr-1"></i>{{ config_saving ? 'Sauvegarde...' : 'Sauvegarder' }}
				</b-button>
			</template>
		</b-modal>
		<!-- ── Modale transfert Hermes → DB ────────────────────────────────────── -->
		<b-modal :id="'modal-transfer-hermes-' + id" title="Importer depuis Hermes" hide-footer>
			<div v-if="hermes_transfer === 'loading'" class="text-center py-4">
				<b-spinner variant="warning" class="mb-3"></b-spinner>
				<p class="mb-0">Import en cours…</p>
			</div>
			<div v-else-if="hermes_transfer && hermes_transfer.status === 200" class="py-2">
				<b-alert show variant="success">
					<i class="fas fa-check-circle mr-1"></i>
					Import terminé — <strong>{{ hermes_transfer.transferred }}</strong> transféré(s),
					<strong>{{ hermes_transfer.skipped }}</strong> ignoré(s) (déjà présent(s))
					sur {{ hermes_transfer.total }} document(s) Hermes.
				</b-alert>
				<b-button size="sm" variant="outline-secondary" @click="hermes_transfer = null; $bvModal.hide('modal-transfer-hermes-' + id)">Fermer</b-button>
			</div>
			<div v-else-if="hermes_transfer && hermes_transfer.error" class="py-2">
				<b-alert show variant="danger">
					<i class="fas fa-exclamation-triangle mr-1"></i>
					Erreur : {{ hermes_transfer.msg }}
				</b-alert>
				<b-button size="sm" variant="outline-secondary" @click="hermes_transfer = null">Réessayer</b-button>
			</div>
			<div v-else class="py-2">
				<p>Cette action va importer tous les documents Hermes de votre espace (client + env)
					dans la table GED. Les documents déjà présents (même UID) seront ignorés.</p>
				<div class="d-flex" style="gap:.5rem">
					<b-button variant="warning" @click="do_transfer_hermes()">
						<i class="fas fa-cloud-download-alt mr-1"></i>Lancer l'import
					</b-button>
					<b-button variant="outline-secondary" @click="$bvModal.hide('modal-transfer-hermes-' + id)">Annuler</b-button>
				</div>
			</div>
		</b-modal>
	</div>
</template>

<script>
import uniqueId from "lodash.uniqueid";
import vue2Dropzone from "vue2-dropzone";
import "vue2-dropzone/dist/vue2Dropzone.min.css";

import "../../public/js/query-builder.default.min.css";
import "../../public/js/query-builder.min.js";
import "../../public/js/query-builder-fr.js";

import $ from "jquery";

import document_service from "../services/document.js";
import viewFile from "../components/viewFile.vue";
import users from "../services/users.js";
import client_service from "../services/client.js";
import store from "../store";

var gen = require("random-seed");
var clone = require("clone");

// Normalise les tags d'un document :
// - En mode db : reconstruit doc.tags depuis les colonnes brutes MySQL (ex: doc["tag"] = '["test"]')
//   car format_tags côté back ne parse pas les colonnes JSON (string → itère les chars) et
//   n'est pas appelé du tout pour get_folder.
// - En mode non-db : convertit string[] → [{type, name}]
function normalizeTags(doc, tagsConfig) {
	// Mode db : relire les colonnes brutes définies dans client.details.tags
	if (tagsConfig && Object.keys(tagsConfig).length > 0) {
		var tags = [];
		var columns = Object.keys(tagsConfig);
		for (var j = 0; j < columns.length; j++) {
			var col = columns[j];
			var cfg = tagsConfig[col];
			var colName = (cfg && cfg.colName) ? cfg.colName : col;
			var val = doc[colName];
			if (!val) continue;
			// mysql2 retourne les colonnes JSON/TEXT comme string → parser
			if (typeof val === 'string') {
				try { val = JSON.parse(val); } catch(e) { continue; }
			}
			if (Array.isArray(val)) {
				for (var k = 0; k < val.length; k++) {
					if (typeof val[k] === 'string') {
						tags.push({ type: col, name: val[k] });
					} else if (val[k] && val[k].name) {
						tags.push(val[k]);
					}
				}
			}
		}
		doc.tags = tags;
		return doc;
	}
	// Mode non-db : string[] → [{type, name}]
	if (Array.isArray(doc.tags)) {
		doc.tags = doc.tags.map(t => (typeof t === 'string' ? { type: 'tag', name: t } : t));
	}
	return doc;
}

export default {
	name: "ged",

	components: {
		vueDropzone: vue2Dropzone,
		viewFile,
	},

	props: {
		select: {
			default: false,
			type: Boolean,
		},
		select_view: {
			default: true,
			type: Boolean,
		},
		download: {
			default: true,
			type: Boolean,
		},
		id_prefix: {
			default: false,
			type: Boolean,
		},
		prop_path: {
			default: null,
		},
		minimal: {
			default: false,
		},
		view_files: {
			default: false,
		},
		thd_class: {
			default: "",
		},
		prop_extension: {
			default: "",
		},
	},

	data() {
		return {
			// identifiant unique pour ce composant (évite les conflits de modals/ids)
			id: uniqueId("ged-"),

			// état général
			db: false,
			share_rules: false,
			// config colonnes GED (columns_ged du client)
			columns_ged: [],
			ged_cell_edit: { uid: null, col: null, val: '' },  // cellule en cours d'édition
			ged_tag_open: null,  // { uid, col, val } — formulaire d'ajout de tag ged ouvert
			col_visibility: {},        // visibilité par col.column → bool (true = affiché dans le tableau)
			main_tags_col: '',         // nom de la colonne tags principale (client.details.tags)
			tags_col_visible: true,    // visibilité de la colonne tags principale
			tags_columns: [],
			tag_filter_column: '',
			share: false,
			document_folder: false,
			document_tags: false,
			document_tags_start: false,
			document_tags_actions: true,

			// configuration columns_ged (utilisateurs avec règle tags)
			can_config: false,
			is_admin: false,
			hermes_transfer: null,   // résultat du dernier transfert { transferred, skipped, total } ou 'loading'
config_tags_column: '',     // colonne MySQL pour client.details.tags (simple string)
				config_tags_editable: true, // éditable par l'utilisateur (lié à client.details.tags_editable)
				config_tags_col_visible: true, // visible dans le tableau (lié à client.details.tags_col_visible)
				tags_editable: true,        // valeur active (lue depuis client.details)
				config_columns_ged: [],     // copie éditable pour la modale
			config_saving: false,

			// règles utilisateur
			rules: {
				create: true,
				create_folder: true,
				update: true,
				delete: true,
				root_folder: "",
			},

			// navigation dossiers
			folders: [],
			folder_input: "",
			folder_string: "/",
			folder_uid: null,

			// liste des documents affichés
			documents: [],
			nb: null,
			page: 1,
			limit: 20,
			deep: false,
			order: "libelle asc",
			extension: "",
			filters: {},
			is_search: false,
			search: "",

			// tri table
			sort_by: "libelle",
			sort_desc: false,
			order_options: [
				{ value: 'libelle asc',    text: 'Nom ↑' },
				{ value: 'libelle desc',   text: 'Nom ↓' },
				{ value: 'dateheure asc',  text: 'Date ↑' },
				{ value: 'dateheure desc', text: 'Date ↓' },
				{ value: "CASE WHEN type='folder' THEN 0 ELSE 1 END asc, SUBSTRING_INDEX(libelle, '.', -1) asc",  text: 'Type (dossiers puis ext. ↑)' },
				{ value: "CASE WHEN type='folder' THEN 0 ELSE 1 END asc, SUBSTRING_INDEX(libelle, '.', -1) desc", text: 'Type (dossiers puis ext. ↓)' },
			],
			// champs tableau principal
			fields_doc: [
				{
					key: "type",
					label: "Preview",
					sortByFormatted: true,
					formatter: (value, key, item) => item.type === "folder" ? "0" : item.libelle.split(".").pop(),
					thStyle: { minWidth: "100px" },
				},
				{
					key: "libelle",
					label: "Nom",
					thStyle: { minWidth: "200px" },
				},
				{
					key: "path_name",
					label: "Chemin",
					thClass: "d-none",
					tdClass: "d-none",
				},
				{
					key: "dateheure",
					label: "Date",
					thStyle: { minWidth: "250px" },
				},
			],

			// dropzone / upload
			launch: false,
			public_file: false,
			counter: 0,
			files: [],
			nb_files: 0,
			size_files: 0,
			dropzoneOptions: {
				url: `${process.env.VUE_APP_SAAS_BACK}` + "/document/add_files",
				dictDefaultMessage: "Télécharger les fichiers",
				autoProcessQueue: false,
				uploadMultiple: false,
				maxFiles: 10000,
				parallelUploads: 5,
				previewTemplate: "<div></div>",
				timeout: 3600000,
				maxFilesize: 10000,
			},
			upload_fields: [
				{ key: "name",  label: "Nom" },
				{ key: "size",  label: "Taille" },
				{ key: "del",   label: "" },
			],
			// données tags/colonnes individuelles par fichier (clé = uuid dropzone)
			file_specific_data: {},

			// sélection
			select_all: false,
			data_files: [],
			data_files_desc: [],
			data_files_uid: [],

			// document courant (visualisation / déplacement)
			current_document: {},
			detail_doc: null,    // document ouvert dans la modale détail (galerie / plein écran)
			view_file: false,
			comments: [],
			column_comments: "",

			// plein écran
			show_files: null,
			show_files_idx: 0,
			tab_idx: 0,
			new_folder: "",
			new_name: "",
			delete_if_empty: false,

			// tags
			tags: {},
			tags_list: [],
			tag: { name: "", type: "tag" },
			active_tag: null,

			// tags à l'import
			init_tags: {},
			// colonnes de type 'column' à l'import
			init_columns: {},

			// tags multi-sélection
			toggle_add_multi_tags: false,
			toggle_remove_multi_tags: false,
			multi_add_tags: [],
			multi_tag_input: "",
			multi_tag_output_col: "",

			// query builder
			rules_sql: "",
			rules_editor: "",

			// progression génération zip (écran bloquant)
			zip_uid: null,
			zip_progress: { step: null, current: 0, total: 0, bytes: 0, total_bytes: 0 },
			zip_progress_visible: false,
		};
	},

	computed: {
		event() {
			return this.$store.getters.getEvent;
		},

		// Champs du tableau selon col_visibility (dropdown toolbar)
		fields_with_extra() {
			if (this.minimal) return this.fields_doc;
			// Masquer la colonne tags principale si décochée
			let base = this.fields_doc;
			if (this.main_tags_col && !this.tags_col_visible)
				base = base.filter(f => f.key !== this.main_tags_col);
			const existingKeys = new Set(base.map(f => f.key));
			const visible = this.columns_ged
				.filter(c => this.col_visibility[c.column])
				.map(c => ({ key: c.column, label: c.label }))
				.filter(f => !existingKeys.has(f.key));
			if (!visible.length) return base;
			const actionIdx = base.findIndex(f => f.key === 'uid');
			return actionIdx >= 0
				? [...base.slice(0, actionIdx), ...visible, ...base.slice(actionIdx)]
				: [...base, ...visible];
		},
	},

	watch: {
		rules_sql()  { this.set_filters(); },
		limit()      { this.set_filters(); },
		page()       { this.set_filters(); },
		order()      { this.set_filters(); },
		deep()       { this.set_filters(); },
		extension()  { this.set_filters(); },

		async event(event) {
			if (event.type === "document") {
				await this.get_folder(null, true);
			}
		},

		folders: {
			deep: true,
			handler(newVal) {
				if (newVal.length === 0) {
					this.folder_string = "/";
					this.folder_uid = null;
				} else {
					this.folder_string = newVal
						.filter(Boolean)
						.map(f => "/" + f.libelle)
						.join("");
					const last = newVal[newVal.length - 1];
					this.folder_uid = last ? last.uid : null;
				}
			},
		},

		async show_files(val) {
			if (val === null) {
				this.page = 1;
				this.limit = 20;
				this.set_filters();
				await this.search_documents();
			} else if (val === false) {
				this.page = this.show_files_idx + 1;
				this.limit = 1;
				this.set_filters();
				await this.search_documents();
				this.current_document = this.documents.length > 0 ? this.documents[0] : {};
				this.$nextTick(() => { this.show_files = true; });
			}
		},
	},

	methods: {
		// ── Utilitaires ───────────────────────────────────────────────

		file_icon_class(libelle) {
			if (libelle.endsWith(".csv"))  return "fas fa-file-csv";
			if (libelle.endsWith(".xlsx")) return "fas fa-file-excel";
			if (libelle.endsWith(".pdf"))  return "fas fa-file-pdf text-danger";
			if (libelle.endsWith(".png") || libelle.endsWith(".jpg") || libelle.endsWith(".jpeg"))
				return "fas fa-image";
			if (libelle.endsWith(".docx")) return "fas fa-file-word";
			return "fas fa-file";
		},

		generateColorRgb(str) {
			const r = gen(str);
			return `rgb(${r.intBetween(50,230)}, ${r.intBetween(50,230)}, ${r.intBetween(50,230)})`;
		},

		// ── Filtres / recherche ───────────────────────────────────────

		set_filters() {
			this.filters = {};

			if (this.rules_sql)
				this.filters.rules_sql = "(" + this.rules_sql + ")";

			if (this.minimal && !this.view_files) {
				this.filters.rules_sql = (this.filters.rules_sql ? this.filters.rules_sql + " and " : "") + " type = 'folder' ";
			}

			if (!this.deep) {
				const folder_clause = this.folder_uid
					? ` folder = '${this.folder_uid}'`
					: " folder is null";
				if (this.rules_sql) {
					this.filters.rules_sql = (this.filters.rules_sql || "") + " and " + folder_clause;
				}
			}

			if (this.extension) {
				const ext_clause = ` (type = 'folder' or libelle like '%.${this.extension}') `;
				this.filters.rules_sql = (this.filters.rules_sql ? this.filters.rules_sql + " and " : "") + ext_clause;
			}

			this.filters.order_by = (this.order ? this.order + ", " : "") + " path_name, id ";

			if (this.limit && this.limit > 0) {
				this.filters.limit = this.limit;
				if (this.page && this.page > 0)
					this.filters.offset = (this.page - 1) * this.limit;
			}
		},

		reset_builder() {
			try { $(`#builder-${this.id}`).queryBuilder("reset"); } catch (e) { /* ignore */ }
			this.page = 1;
			this.rules_sql = "";
			this.deep = false;
			this.set_filters();
		},

		// ── Onglets partage ─────────────────────────────────────────

		onShareTabChange(tabIndex) {
			if (tabIndex === 0) {
				this.document_tags = this.document_tags_start ? true : false;
				this.share = false;
			} else {
				this.document_tags = false;
				this.share = true;
			}
			this.get_folder(false, false, true, true);
		},

		// ── Navigation dossiers ───────────────────────────────────────

		async go_to_folder_idx(idx) {
			const filtered = this.folders.filter(Boolean);
			if (idx >= filtered.length - 1) return;
			// on coupe les dossiers jusqu'à l'index cliqué
			this.folders = filtered.slice(0, idx + 1);
			this.folder_uid = filtered[idx] ? filtered[idx].uid : null;
			await this.get_folder(false, false, true);
		},

		async parent_folder(all = true) {
			if (all)
				this.folders = [];
			else
				this.folders.pop();

			this.folder_uid = (this.folders.length === 0 || !this.folders[this.folders.length - 1])
				? null
				: this.folders[this.folders.length - 1].uid;

			await this.get_folder(false, false, true);

			if (!this.select && !this.minimal) {
				const query = this.share ? { share: true } : {};
				this.$router.push({ path: "/document/" + (this.folder_string !== "/" ? this.folder_string : ""), query });
			}
		},

		async change_folder(folder) {
			await this.get_folder(folder, this.document_tags, true);
			if (!this.select && !this.minimal) {
				const query = this.share ? { share: true } : {};
				this.$router.push({ path: "/document/" + (this.folder_string !== "/" ? this.folder_string : ""), query });
			}
		},

		// ── Chargement documents ──────────────────────────────────────

		async get_folder(folder = null, update = false, reset = false, reset_path = false) {
			if (this.show_files)
				this.show_files_idx = 0;

			if (reset_path) {
				await this.parent_folder(true);
				return;
			}

			if (reset)
				this.reset_builder();

			this.select_all = false;
			this.view_file = false;

			let data;
			if (!folder) {
				data = await document_service.get_folder(this.folder_uid, this.document_tags, this.filters, this.share);
				this.nb = data.nb ?? null;
			} else {
				data = await document_service.get_folder(folder.uid, this.document_tags, this.filters, this.share);
				this.nb = data.nb ?? null;
				const folderPath = (folder.path ? folder.path + "/" : "") + folder.uid;
				const path_folders = await document_service.path_folder(folderPath, false, this.share);
				this.folders = path_folders;
			}

			if (update) {
				const map = {};
				(data.documents || []).forEach(d => { map[d.uid] = d; });
				this.documents.forEach(d => {
					if (map[d.uid]) d.thumbnail = map[d.uid].thumbnail;
				});
			} else {
				const self = this;
				await new Promise(resolve => {
					self.$nextTick(() => {
						self.documents = (data.documents || []).map(d => normalizeTags(d, self.tags));
						console.log('[GED] get_folder documents:', self.documents.map(d => ({ libelle: d.libelle, tags: d.tags })));
						resolve();
					});
				});
			}

			if (this.show_files)
				this.show_files = false;
		},

		async search_documents() {
			const self = this;
			document.body.style.cursor = "wait";

			if (this.show_files)
				this.show_files_idx = 0;
			this.select_all = false;

			if (this.search || this.rules_sql) {
				this.is_search = true;
				// Afficher la colonne chemin en mode recherche
				this.$set(this.fields_doc, 2, { key: "path_name", label: "Chemin" });
				this.documents = [];

				await new Promise(resolve => {
					self.$nextTick(async () => {
						const result = await document_service.search_documents(
							self.folder_uid,
							self.search,
							self.filters,
							self.share
						);
						self.nb = result.nb ?? null;
						self.documents = (result.documents || []).map(d => normalizeTags(d, self.tags));
						console.log('[GED] search_documents:', self.documents.map(d => ({ libelle: d.libelle, tags: d.tags })));
						resolve();
					});
				});
			} else {
				this.is_search = false;
				this.$set(this.fields_doc, 2, {
					key: "path_name", label: "Chemin", thClass: "d-none", tdClass: "d-none",
				});
				await this.get_folder();
			}

			document.body.style.cursor = "default";
			if (this.show_files)
				this.show_files = false;
		},

		// ── Tags (sur document individuel) ────────────────────────────

		async remove_tag(doc, tag) {
			for (let i = 0; i < this.documents.length; i++) {
				if (doc.uid === this.documents[i].uid) {
					const j = this.documents[i].tags
						.findIndex(t => t.type === tag.type && t.name === tag.name);
					if (j !== -1) this.documents[i].tags.splice(j, 1);
					this.$set(this.documents, i, this.documents[i]);
					await document_service.update(this.documents[i].uid, this.documents[i].public, this.documents[i].tags, this.share);
					break;
				}
			}
			this.tag = { name: "", type: "tag" };
		},

		toggle_multi_tags({ add, remove }) {
			if (add) {
				this.toggle_add_multi_tags = !this.toggle_add_multi_tags;
				this.toggle_remove_multi_tags = false;
			} else if (remove) {
				this.toggle_remove_multi_tags = !this.toggle_remove_multi_tags;
				this.toggle_add_multi_tags = false;
			}
			if (!this.toggle_add_multi_tags && !this.toggle_remove_multi_tags) {
				this.multi_add_tags = [];
			}
		},

		async add_tags_to_selection(type) {
			if (this.multi_add_tags.length === 0) {
				this.$notify({ group: "info", type: "warn", title: "Aucun tag", text: "Veuillez entrer au moins un tag." });
				return;
			}

			var list = type === "selection" ? this.documents.filter(d => d.checkbox) : [];
			var opts = null;
			if (type === "search") {
				var filters = clone(this.filters);
				delete filters.limit;
				delete filters.offset;
				opts = { is_search: this.is_search, folder: this.folder_uid, document_tags: this.document_tags, filters, share: this.share };
			}

			var update_tags = this.multi_add_tags.map(tag => ({ name: tag, type: this.multi_tag_output_col }));
			var resp = await document_service.update_all(list, opts, update_tags, this.toggle_add_multi_tags ? "add" : "remove");

			if (resp && resp.data && resp.data.status === 200) {
				for (const doc of this.documents) {
					if (type === "selection" && !doc.checkbox) continue;
					for (const tagName of this.multi_add_tags) {
						this.tag.name = tagName;
						if (this.toggle_add_multi_tags) {
							this.add_tag(doc, this.multi_tag_output_col, true); // skip_save: update_all already saved
						} else {
							this.remove_tag(doc, { name: tagName, type: this.multi_tag_output_col });
						}
					}
				}
				this.$notify({ group: "info", type: "success", title: "Tags mis à jour" });
			} else {
				this.$notify({ group: "info", type: "error", title: "Erreur lors de la mise à jour des tags" });
			}

			if (this.$refs["multi-tag"]) this.$bvModal.hide("multi-tag-" + this.id);
			this.toggle_add_multi_tags = false;
			this.toggle_remove_multi_tags = false;
			this.multi_add_tags = [];
		},

		async add_tag(doc, type = null, skip_save = false) {
			if (type) this.tag.type = type;
			if (!this.tag.name) return;
			this.tag.name = this.tag.name.trim();
			if (!doc.tags) doc.tags = [];
			doc.tags.push({ ...this.tag });
			for (let i = 0; i < this.documents.length; i++) {
				if (doc.uid === this.documents[i].uid) {
					this.$set(this.documents, i, doc);
					if (!skip_save) {
						await document_service.update(doc.uid, doc.public, doc.tags, null, this.share);
					}
					break;
				}
			}
			this.tag = { name: "", type: "tag" };
		},

		async add_tag_button(doc, value, column = null) {
			await this.add_tag(doc, column); // saves if tag.name is non-empty
			this.tag = { name: "", type: "tag" };
			doc.add_tag = value;
			for (let i = 0; i < this.documents.length; i++) {
				if (doc.uid === this.documents[i].uid) {
					this.$set(this.documents, i, doc);
					break;
				}
			}
		},

		// ── Actions sur fichiers ──────────────────────────────────────

		async hide() {
			this.$bvModal.hide("modal-document");
		},

		canPreview(name) {
			if (!name) return false;
			const n = name.toLowerCase();
			return n.endsWith('.pdf') || n.endsWith('.csv') || n.endsWith('.xlsx') || n.endsWith('.docx') ||
				n.endsWith('.html') || n.endsWith('.txt') || n.endsWith('.xml') ||
				n.endsWith('.png') || n.endsWith('.jpg') || n.endsWith('.jpeg');
		},

		async dowload(file) {
			this.$bvModal.hide("modal-document");
			document_service.getFile(file.uid, file.libelle, true, "blob", this.share);
		},

		async view_document(doc) {
			if (!this.canPreview(doc.libelle)) {
				this.dowload(doc);
				return;
			}
			this.current_document = doc;
			this.$bvModal.show("modal-document");
			this.$nextTick(() => {
				this.$refs.view_file.viewFile(doc.url, doc.libelle);
				this.view_file = true;
			});
		},

		async update_public(file, public_file) {
			await document_service.update(file.uid, public_file, this.share);
			this.$set(file, 'public', public_file);
			await this.get_folder();
		},

		// Retourne un tableau de strings depuis row.item[colName] (gère string JSON ou Array)
		gedTagsArray(item, colName) {
			const v = item[colName];
			if (!v) return [];
			if (Array.isArray(v)) return v;
			try { const p = JSON.parse(v); return Array.isArray(p) ? p : []; } catch { return []; }
		},

		// Supprime un tag à l'index idx d'une colonne ged type:'tags'
		async ged_remove_tag(item, colName, idx) {
			const arr = [...this.gedTagsArray(item, colName)];
			arr.splice(idx, 1);
			await this.save_ged_col(item, colName, arr);
		},

		// Ajoute le tag depuis le formulaire ged_tag_open
		async ged_add_tag_from_open(item) {
			const val = (this.ged_tag_open.val || '').trim();
			const colName = this.ged_tag_open.col;
			if (!val) return;
			const arr = [...this.gedTagsArray(item, colName)];
			if (!arr.includes(val)) {
				arr.push(val);
				await this.save_ged_col(item, colName, arr);
			}
			this.ged_tag_open = { ...this.ged_tag_open, val: '' };
		},

		// Ajoute le tag saisi (ged_cell_edit.val) à une colonne ged type:'tags'
		async ged_add_tag(item, colName) {
			const val = (this.ged_cell_edit.val || '').trim();
			this.ged_cell_edit = { uid: null, col: null, val: '' };
			if (!val) return;
			const arr = [...this.gedTagsArray(item, colName)];
			if (!arr.includes(val)) arr.push(val);
			await this.save_ged_col(item, colName, arr);
		},

		// Édition d'une valeur de colonne GED (string ou tableaux JSON)
		async save_ged_col(item, colName, directVal) {
			const val = directVal !== undefined ? directVal : this.ged_cell_edit.val;
			if (directVal === undefined) this.ged_cell_edit = { uid: null, col: null, val: '' };
			try {
				const sendVal = Array.isArray(val) ? JSON.stringify(val) : val;
				await document_service.update(item.uid, null, null, null, this.share, { [colName]: sendVal });
				item[colName] = val;
			} catch (e) {
				console.error('ged_col update error', e);
			}
		},

		async delete_document(doc) {
			this.$fire({
				title: "Etes vous sûr de vouloir supprimer " + doc.libelle + "?",
				showCloseButton: true,
				showCancelButton: true,
				focusConfirm: false,
				confirmButtonText: "oui",
				cancelButtonText: "non",
			}).then(async r => {
				if (r.value) {
					await document_service.deleteFile(doc.uid, this.share);
					await this.get_folder();
				}
			});
		},

		// ── Sélection ────────────────────────────────────────────────

		async add_selected(file) {
			file.share = this.share;
			this.data_files_uid.push(file.uid);
			if (this.download) {
				const doc = await document_service.getFile(file.uid, file.libelle, false, "blob", this.share);
				const filename = this.id_prefix
					? file.id + (typeof this.id_prefix === "string" ? this.id_prefix : "-") + file.libelle
					: file.libelle;
				this.data_files.push(new File([doc], filename, { type: "application/octet-stream" }));
				this.data_files_desc.push({ id: file.id, libelle: file.libelle });
			} else {
				this.data_files_desc.push(file);
			}
			this.$emit("add_file", file);
			this.$emit("select_file", this.data_files_desc);
		},

		del_data_files(id) {
			for (let i = 0; i < this.data_files_desc.length; i++) {
				if (this.data_files_desc[i].id === id) {
					if (this.download) this.data_files.splice(i, 1);
					this.data_files_desc.splice(i, 1);
					this.data_files_uid.splice(i, 1);
					break;
				}
			}
			this.$emit("select_file", this.data_files_desc);
		},

		get_data()            { return this.data_files_desc; },
		async get_data_files() { return this.data_files; },

		load_data_files_desc(selection) {
			this.data_files_desc = clone(selection);
			this.data_files_uid = this.data_files_desc.map(d => d.uid);
		},

		// ── Checkbox all ─────────────────────────────────────────────

		f_select() {
			this.select_all ? this.checkbox_all() : this.checkbox_no_all();
		},

		checkbox_all() {
			const docs = clone(this.documents);
			docs.forEach(d => { d.checkbox = true; });
			this.documents = docs;
		},

		checkbox_no_all() {
			this.documents.forEach(d => { d.checkbox = false; });
		},

		// ── Zip ───────────────────────────────────────────────────────

		on_zip_progress(data) {
			if (data.step === 'error') {
				this.$notify({ group: "info", title: data.msg || "Erreur lors de la génération du zip", type: "error", duration: 10000 });
			}
			this.zip_progress = { ...this.zip_progress, ...data };
		},

		async dowloadZip(type) {
			document.body.style.cursor = "wait";
			const list = type === "selection"
				? this.documents.filter(d => d.checkbox)
				: [];

			const filters = clone(this.filters);
			delete filters.limit;
			delete filters.offset;
			const opts = {
				is_search: this.is_search,
				folder: this.folder_uid,
				document_tags: this.document_tags,
				filters,
				share: this.share,
			};

			this.$refs["zip"].$refs["close-button"].click();

			this.zip_uid = this.$uuid.v4();
			this.zip_progress = { step: 'downloading', current: 0, total: 0, bytes: 0, total_bytes: 0 };
			this.zip_progress_visible = true;
			this.sockets.subscribe(this.zip_uid, this.on_zip_progress);

			try {
				const resp = await document_service.getZip(
					list, true, "blob", this.share, opts,
					this.zip_uid,
					(evt) => {
						this.zip_progress = {
							...this.zip_progress,
							step: this.zip_progress.step === 'error' ? this.zip_progress.step : 'sending',
							bytes: evt.loaded,
							total_bytes: evt.total || 0,
						};
					}
				);
				if (resp && resp.msg) {
					this.$notify({ group: "info", title: resp.msg, type: "error", duration: 10000 });
				}
			} finally {
				this.sockets.unsubscribe(this.zip_uid);
				this.zip_uid = null;
				this.zip_progress_visible = false;
				this.zip_progress = { step: null, current: 0, total: 0, bytes: 0, total_bytes: 0 };
				document.body.style.cursor = "default";
			}
		},

		// ── Suppression groupée ───────────────────────────────────────

		async deleteFiles(type) {
			document.body.style.cursor = "wait";
			const list = type === "selection"
				? this.documents.filter(d => d.checkbox)
				: [];

			const filters = clone(this.filters);
			delete filters.limit;
			delete filters.offset;
			const opts = {
				is_search: this.is_search,
				folder: this.folder_uid,
				document_tags: this.document_tags,
				filters,
			};

			await document_service.deleteFiles(list, opts);
			document.body.style.cursor = "default";
			this.$refs["delete"].$refs["close-button"].click();
			await this.get_folder();
		},

		// ── Déplacer / Renommer ───────────────────────────────────────

		async move_document(doc) {
			if (doc) {
				this.current_document = doc;
				this.new_name = doc.libelle;
			} else {
				this.current_document = null;
				this.new_name = "";
			}
			this.$bvModal.show("modal-move");
			this.$nextTick(() => { /* ready */ });
		},

		get_folders() {
			return this.folders;
		},

		async move(type) {
			this.new_folder = null;
			const folders = clone(this.$refs.ged.get_folders());
			if (folders && folders.length > 0)
				this.new_folder = folders.pop();

			let resp;
			if (this.current_document) {
				resp = await document_service.rename(
					this.current_document.uid,
					this.new_folder ? this.new_folder.uid : null,
					this.new_name,
					this.delete_if_empty
				);
			} else {
				const list = type === "selection"
					? this.documents.filter(d => d.checkbox).map(d => d.uid)
					: [];
				const filters = clone(this.filters);
				delete filters.limit;
				delete filters.offset;
				const opts = {
					is_search: this.is_search,
					folder: this.folder_uid,
					document_tags: this.document_tags,
					filters,
				};
				resp = await document_service.rename(
					list,
					this.new_folder ? this.new_folder.uid : null,
					"",
					this.delete_if_empty,
					opts
				);
			}

			if (resp.data && !resp.data.errors) {
				this.$notify({ group: "info", title: "Renommage et déplacement effectué", type: "success" });
			} else {
				this.$notify({ group: "info", title: "Une erreur est survenue.", type: "error" });
			}

			if (resp.data && resp.data.del)
				await this.get_folder(false, false, true, true);
			else
				await this.get_folder();
		},

		// ── Dossier – création ────────────────────────────────────────

		async add_folder() {
			if (!this.folder_input) return;

			const response = await document_service.add(
				this.folder_input,
				this.folder_uid,
				"folder",
				this.share
			);

			if (response.errors && response.errors.errors && response.errors.errors[0]) {
				const err = response.errors.errors[0];
				let msg = "Une erreur s'est produite.";
				if (err === "file already exist") msg = "Le fichier ou dossier existe déjà.";
				this.$notify({ group: "info", title: msg, type: "error" });
			}

			await this.get_folder();
			this.$notify({ group: "info", title: "Dossier ajouté", type: "success" });
		},

		// ── Dropzone ──────────────────────────────────────────────────

		template() {
			return "<div></div>";
		},

		thumbnail(file, dataUrl) {
			if (!file.previewElement) return;
			file.previewElement.classList.remove("dz-file-preview");
			file.previewElement
				.querySelectorAll("[data-dz-thumbnail-bg]")
				.forEach(el => {
					el.alt = file.name;
					el.style.backgroundImage = `url("${dataUrl}")`;
				});
			setTimeout(() => file.previewElement.classList.add("dz-image-preview"), 1);
		},

		async start() {
			this.launch = true;
			if (this.$refs.myVueDropzone && this.$refs.myVueDropzone.getQueuedFiles().length > 0) {
				this.$refs.myVueDropzone.processQueue();
			}
		},

		sendingEvent(file, xhr, formData) {
			formData.set("folder", this.folder_uid);
			formData.set("public", this.public_file);
			// Données individuelles du fichier (par uuid)
			const fdata = this.file_specific_data[file.upload.uuid];
			const all_init = fdata ? Object.assign({}, fdata.tags) : Object.assign({}, this.init_tags);
			if (fdata) {
				Object.keys(fdata.columns).forEach(k => {
					if (fdata.columns[k] !== '' && fdata.columns[k] !== null)
						all_init[k] = fdata.columns[k];
				});
			} else {
				Object.keys(this.init_columns).forEach(k => {
					if (this.init_columns[k] !== '' && this.init_columns[k] !== null)
						all_init[k] = this.init_columns[k];
				});
			}
			formData.set("init_tags", JSON.stringify(all_init));
		},

		queueComplete() {
			this.files = [];
			this.nb_files = 0;
			this.size_files = 0;
			this.launch = false;
			this.file_specific_data = {};
			this.init_tags = {};
			this.init_columns = {};
			Object.keys(this.tags).forEach(colName => this.$set(this.init_tags, colName, []));
			this.columns_ged.filter(c => c.type === 'tags').forEach(col => this.$set(this.init_tags, col.column, []));
			this.columns_ged.filter(c => c.type === 'column').forEach(col => this.$set(this.init_columns, col.column, ''));
			this.get_folder();
			this.$notify({ group: "info", title: "Documents enregistrés", type: "success" });
		},

		add_file_to_queue(file) {
			const uuid = file.upload.uuid;
			this.files.push({
				name: file.name,
				size: (file.size / 1000000).toFixed(3) + " mo",
				del: uuid,
			});
			// Initialiser les données par fichier en copiant les valeurs globales comme défaut
			const fileData = { tags: {}, columns: {} };
			Object.keys(this.init_tags).forEach(k => {
				fileData.tags[k] = Array.isArray(this.init_tags[k]) ? [...this.init_tags[k]] : [];
			});
			Object.keys(this.init_columns).forEach(k => {
				fileData.columns[k] = this.init_columns[k] || '';
			});
			this.$set(this.file_specific_data, uuid, fileData);
			this.nb_files++;
			this.size_files = (parseFloat(this.size_files) + parseFloat(file.size / 1000000)).toFixed(3);
		},

		del_file(uuid) {
			const dropzone_files = this.$refs.myVueDropzone.getQueuedFiles();
			let size = 0;
			dropzone_files.forEach(f => {
				if (f.upload.uuid === uuid) {
					size = f.size;
					this.$refs.myVueDropzone.removeFile(f);
				}
			});
			for (let i = 0; i < this.files.length; i++) {
				if (this.files[i].del === uuid) {
					this.nb_files--;
					this.size_files = (parseFloat(this.size_files) - parseFloat(size / 1000000)).toFixed(3);
					this.files.splice(i, 1);
					this.$delete(this.file_specific_data, uuid);
					break;
				}
			}
		},

		progress(totaluploadprogress) {
			this.counter = totaluploadprogress;
		},

		save() { return {}; },
		load() {},

		// Ouvre la modale de configuration des colonnes GED
		open_config() {
			// Colonne tags principale : juste le nom de colonne MySQL
			const mainKey = Object.keys(this.tags)[0] || '';
			this.config_tags_column = mainKey || '';
			this.config_tags_editable = this.tags_editable;
			this.config_tags_col_visible = this.tags_col_visible;
			// Clone les colonnes actuelles pour édition
			this.config_columns_ged = this.columns_ged.map(c => ({ ...c }));
			this.$bvModal.show('modal-config-ged-' + this.id);
		},

		// Ajoute une ligne vide dans l'éditeur
		config_add_col() {
			this.config_columns_ged.push({
				key:        '',
				column:     '',
				label:      '',
				display:    'table',
				filterable: true,
				editable:   true,
				type:       'column',
			});
		},

		// Supprime une ligne de l'éditeur
		config_remove_col(idx) {
			this.config_columns_ged.splice(idx, 1);
		},

		// Sauvegarde columns_ged + tags via l'API et recharge la GED
		async save_columns_ged() {
			this.config_saving = true;
			try {
				// Convertit le tableau en objet { key: { column, label, display, filterable, type } }
				const obj = {};
				for (const col of this.config_columns_ged) {
					if (!col.key) continue;
					obj[col.key] = {
						column:     col.column     || col.key,
						label:      col.label      || col.key,
						display:    col.display    !== undefined ? col.display    : 'table',
						filterable: col.filterable !== false,
						editable:   col.editable   !== false,
						type:       col.type       || 'column',
					};
				}
				// Colonne tags principale : simple string
				const tagsObj = this.config_tags_column || null;
				const resp = await client_service.put(
					null, null, null, null, null, null, null,
					tagsObj,
					null, null, null, null,
					obj,
					this.config_tags_editable,
					this.config_tags_col_visible
				);
				if (resp && resp.status === 200) {
					this.$notify({ group: 'info', type: 'success', title: 'Configuration sauvegardée' });
					this.$bvModal.hide('modal-config-ged-' + this.id);
					window.location.reload();
				} else {
					this.$notify({ group: 'info', type: 'error', title: 'Erreur lors de la sauvegarde' });
				}
			} catch(e) {
				this.$notify({ group: 'info', type: 'error', title: 'Erreur : ' + e.message });
			}
			this.config_saving = false;
		},

		// ── Import Hermes → DB ─────────────────────────────────────────────────────
		async do_transfer_hermes() {
			this.hermes_transfer = 'loading';
			try {
				const result = await document_service.transfer_hermes();
				this.hermes_transfer = result;
				if (result && !result.error) {
					await this.get_folder();
				}
			} catch(e) {
				this.hermes_transfer = { error: true, msg: e.message || 'Erreur inconnue' };
			}
		},
	},

	async mounted() {
		const self = this;

		// Redirection si pas de droits documents
		if (store.state.user.rules && !store.state.user.rules.document) {
			this.$router.push({ name: "profile" });
			return;
		}
		if (store.state.user.rules && store.state.user.rules.document) {
			this.rules = store.state.user.rules.document_rules || {};
		}

		// Droit de configurer les colonnes GED : pas de règles = admin = tout autorisé
		if (!store.state.user.rules || store.state.user.rules.tags)
			this.can_config = true;

		// Administrateur : présence de details.client_uid dans le JWT
		this.is_admin = !!(store.state.user.details && store.state.user.details.client_uid);

		if (this.prop_extension)
			this.extension = this.prop_extension;

		this.set_filters();

		// Mode tags vs mode dossiers
		if (store.state.user.rules && store.state.user.rules.document_tags) {
			this.document_tags_start = true;
			this.document_tags = true;
		} else {
			this.document_folder = true;
		}

		if (store.state.user.rules && store.state.user.rules.document_share_rules_sql)
			this.share_rules = true;

		if (store.state.user.rules && !store.state.user.rules.document_tags_actions)
			this.document_tags_actions = false;

		// Infos client
		const response = await users.getSelf(true);
		const client = response.client;

		if (client.details.document && client.details.document.mode === "db")
			this.db = true;

		// ── client.details.tags → colonne principale des tags (actions add/remove, badges, filtre) ──
		// Format : simple string = nom de la colonne MySQL
		this.tags_editable = client.details.tags_editable !== false;
		this.config_tags_editable = this.tags_editable;
		this.tags_col_visible = client.details.tags_col_visible !== false;
		this.config_tags_col_visible = this.tags_col_visible;
		if (client.details.tags && this.db) {
			const colName = client.details.tags;
			if (typeof colName === 'string' && colName) {
				this.tags = { [colName]: { type: "Tags", column: true, colName } };
				this.tags_list.push({ value: colName, text: "Tags" });
				this.multi_tag_output_col = colName;
				this.tags_columns.push(colName);
				if (!this.minimal)
					this.fields_doc.push({ key: colName, label: "Tags" });
				// Mémoriser pour le dropdown de visibilité
				this.main_tags_col = colName;
				// Pré-initialiser pour la réactivité Vue 2
				this.$set(this.init_tags, colName, []);
			}
		}
		if (this.db && Object.keys(this.tags).length === 0)
			this.document_tags_actions = false;

		// ── client.details.columns_ged → colonnes supplémentaires (affichage + édition inline) ──
		// Format : { column, label, display: "table"|"button"|false, filterable, type: "tags"|"column" }
		if (client.details.columns_ged && this.db) {
			this.columns_ged = Object.entries(client.details.columns_ged).map(([key, cfg]) => ({
				key,
				column:     cfg.column     || key,
				label:      cfg.label      || key,
				display:    cfg.display    !== undefined ? cfg.display    : 'table',
				filterable: cfg.filterable !== false,
				editable:   cfg.editable   !== false,
				type:       cfg.type       || 'column',
			}));

			// Initialiser col_visibility : display:"table" → visible, autres → masqué
			this.columns_ged.forEach(col => {
				this.$set(this.col_visibility, col.column, col.display === 'table');
			});
			// Les colonnes type:'tags' (toutes displays) sont rendues via tags_columns (même slot v-for)
			this.columns_ged.filter(c => c.type === 'tags').forEach(col => {
				if (!this.tags_columns.includes(col.column))
					this.tags_columns.push(col.column);
				// Pré-initialiser par col.column (nom MySQL) pour la réactivité Vue 2
				this.$set(this.init_tags, col.column, []);
			});
			this.columns_ged.filter(c => c.type === 'column').forEach(col => {
				this.$set(this.init_columns, col.column, '');
			});
		}

		// Champs actions si pas minimal
		if (!this.minimal || this.select) {
			this.fields_doc.push({
				key: "uid",
				label: "Actions",
				thStyle: { minWidth: "190px" },
			});
		}

		// Colonne commentaires
		if (client.details.document && client.details.document.column_comments && !this.minimal) {
			this.column_comments = client.details.document.column_comments;
			this.fields_doc.push({
				key: client.details.document.column_comments,
				label: "Notes",
				thStyle: { minWidth: "100px" },
			});
		}

		// Checkbox groupée
		if (!this.select && !this.minimal) {
			this.fields_doc.push({ key: "checkbox", label: "" });
		}

		// Auth dropzone
		if (this.$refs.myVueDropzone) {
			this.$refs.myVueDropzone.setOption("headers", {
				Authorization: "bearer " + this.$store.state.jwt,
			});
		}

		// Share via query param
		if (this.$route.query.share)
			this.share = true;

		// Résolution du chemin initial
		let path = self.$route.params.path;
		if (this.prop_path) path = this.prop_path;
		if (path) {
			const folder_list = await document_service.path_folder(path, true, this.share);
			self.folders = folder_list;
			self.folder_uid = folder_list[folder_list.length - 1].uid;
		}

		await this.get_folder();

		// Navigation navigateur (back/forward)
		window.onpopstate = async () => {
			const p = self.$route.params.path;
			if (p) {
				const folder_list = await document_service.path_folder(p, true, self.share);
				self.folders = folder_list;
				self.folder_uid = folder_list[folder_list.length - 1].uid;
			} else {
				self.folder_uid = null;
				self.folders = [];
			}
			await self.get_folder();
		};

		// Query builder (mode db)
		const columns = [
			{ id: "`libelle`",   label: "Nom",  type: "string" },
			{ id: "`dateheure`", label: "Date", type: "date"   },
		];

		// builderTagCols : [{key, colName}] pour toutes les colonnes de type tags filtrables
		// (utilisé dans les handlers pour transformer LIKE → JSON_CONTAINS)
		const builderTagKeys = [];

		// 1. Colonne principale tags (client.details.tags)
		const mainTagKey = Object.keys(this.tags)[0];
		if (mainTagKey) {
			const mainColName = this.tags[mainTagKey].colName || mainTagKey;
			columns.push({ id: "`" + mainColName + "`", label: "Tags", type: "string" });
			builderTagKeys.push({ key: mainTagKey, colName: mainColName });
		}

		// 2. Colonnes filtrables depuis columns_ged
		this.columns_ged.filter(c => c.filterable).forEach(col => {
			const sqlCol = col.column || col.key;
			const alreadyAdded = builderTagKeys.some(b => b.colName === sqlCol);
			if (!alreadyAdded)
				columns.push({ id: "`" + sqlCol + "`", label: col.label, type: "string" });
			if (col.type === 'tags' && !alreadyAdded)
				builderTagKeys.push({ key: col.key, colName: sqlCol });
		});

		const config = {
			filters: columns,
			icons: {
				add_group:    "fas fa-plus-square",
				add_rule:     "fas fa-plus-circle",
				remove_group: "fas fa-minus-square",
				remove_rule:  "fas fa-minus-circle",
				error:        "fas fa-exclamation-triangle",
			},
			operators: [
				{ type: "equal" }, { type: "not_equal" },
				{ type: "less" }, { type: "less_or_equal" },
				{ type: "greater" }, { type: "greater_or_equal" },
				{ type: "between" }, { type: "contains" }, { type: "not_contains" },
				{ type: "begins_with" }, { type: "ends_with" },
				{ type: "is_empty",     apply_to: ["string","number","datetime","boolean"] },
				{ type: "is_not_empty", apply_to: ["string","number","datetime","boolean"] },
			],
		};

		$(`#builder-${this.id}`).queryBuilder(config);

		$(`#builder-${this.id}`).on("change", function () {
			try {
				let sql = $(`#builder-${self.id}`).queryBuilder("getSQL", false).sql;
				sql = sql.replace(/`([^`]*)` = ''/gi,       "(`$1` = '' or `$1` is NULL)");
				sql = sql.replace(/`([^`]*)` != ''/gi,      "(`$1` != '' and `$1` is not NULL)");
				sql = sql.replace(/`([^`]*)` NOT LIKE/gi,   "IFNULL(`$1`, '') NOT LIKE");
		// Colonnes de tags : LIKE → JSON_CONTAINS, IFNULL NOT LIKE → NOT JSON_CONTAINS, = → JSON_CONTAINS, != → NOT JSON_CONTAINS
				builderTagKeys.forEach(({ colName: bColName }) => {
					sql = sql.replace(
						new RegExp("`" + bColName + "` LIKE '%([^%']+)%'", "g"),
						(_, val) => "JSON_CONTAINS(`" + bColName + "`, '\"" + val + "\"')"
					);
					sql = sql.replace(
						new RegExp("IFNULL\\(`" + bColName + "`, ''\\) NOT LIKE '%([^%']+)%'", "g"),
						(_, val) => "NOT JSON_CONTAINS(`" + bColName + "`, '\"" + val + "\"')"
					);
					// equal/not_equal (valeur non vide — les cas '' sont déjà traités au-dessus)
					sql = sql.replace(
						new RegExp("`" + bColName + "` = '([^']+)'", "g"),
						(_, val) => "JSON_CONTAINS(`" + bColName + "`, '\"" + val + "\"')"
					);
					sql = sql.replace(
						new RegExp("`" + bColName + "` != '([^']+)'", "g"),
						(_, val) => "NOT JSON_CONTAINS(`" + bColName + "`, '\"" + val + "\"')"
					);
				});
				self.rules_sql = sql;
				self.rules_editor = $(`#builder-${self.id}`).queryBuilder("getRules");
			} catch {
				self.rules_sql = "";
			}
		});

		$(`#builder-${this.id}`).on("afterDeleteRule.queryBuilder", function () {
			try {
				let sql = $(`#builder-${self.id}`).queryBuilder("getSQL", false).sql;
				sql = sql.replace(/`([^`]*)` = ''/gi,     "(`$1` = '' or `$1` is NULL)");
				sql = sql.replace(/`([^`]*)` != ''/gi,    "(`$1` != '' and `$1` is not NULL)");
				sql = sql.replace(/`([^`]*)` NOT LIKE/gi, "IFNULL(`$1`, '') NOT LIKE");
				builderTagKeys.forEach(({ colName: bColName }) => {
					sql = sql.replace(
						new RegExp("`" + bColName + "` LIKE '%([^%']+)%'", "g"),
						(_, val) => "JSON_CONTAINS(`" + bColName + "`, '\"" + val + "\"')"
					);
					sql = sql.replace(
						new RegExp("IFNULL\\(`" + bColName + "`, ''\\) NOT LIKE '%([^%']+)%'", "g"),
						(_, val) => "NOT JSON_CONTAINS(`" + bColName + "`, '\"" + val + "\"')"
					);
					sql = sql.replace(
						new RegExp("`" + bColName + "` = '([^']+)'", "g"),
						(_, val) => "JSON_CONTAINS(`" + bColName + "`, '\"" + val + "\"')"
					);
					sql = sql.replace(
						new RegExp("`" + bColName + "` != '([^']+)'", "g"),
						(_, val) => "NOT JSON_CONTAINS(`" + bColName + "`, '\"" + val + "\"')"
					);
				});
				self.rules_sql = sql;
			} catch {
				self.rules_sql = "";
			}
		});

		$(`#builder-${this.id}`).on("afterDeleteGroup.queryBuilder", function () {
			try {
				let sql = $(`#builder-${self.id}`).queryBuilder("getSQL", false).sql;
				sql = sql.replace(/`([^`]*)` = ''/gi,     "(`$1` = '' or `$1` is NULL)");
				sql = sql.replace(/`([^`]*)` != ''/gi,    "(`$1` != '' and `$1` is not NULL)");
				sql = sql.replace(/`([^`]*)` NOT LIKE/gi, "IFNULL(`$1`, '') NOT LIKE");
				builderTagKeys.forEach(({ colName: bColName }) => {
					sql = sql.replace(
						new RegExp("`" + bColName + "` LIKE '%([^%']+)%'", "g"),
						(_, val) => "JSON_CONTAINS(`" + bColName + "`, '\"" + val + "\"')"
					);
					sql = sql.replace(
						new RegExp("IFNULL\\(`" + bColName + "`, ''\\) NOT LIKE '%([^%']+)%'", "g"),
						(_, val) => "NOT JSON_CONTAINS(`" + bColName + "`, '\"" + val + "\"')"
					);
					sql = sql.replace(
						new RegExp("`" + bColName + "` = '([^']+)'", "g"),
						(_, val) => "JSON_CONTAINS(`" + bColName + "`, '\"" + val + "\"')"
					);
					sql = sql.replace(
						new RegExp("`" + bColName + "` != '([^']+)'", "g"),
						(_, val) => "NOT JSON_CONTAINS(`" + bColName + "`, '\"" + val + "\"')"
					);
				});
				self.rules_sql = sql;
			} catch {
				self.rules_sql = "";
			}
		});
	},

	beforeDestroy() {
		if (this.zip_uid) {
			this.sockets.unsubscribe(this.zip_uid);
		}
	},
};
</script>

<style lang="css">
/* ── Wrapper général ── */
.ged-wrapper {
	padding: .5rem 0;
}

/* ── Tableau ── */
.ged-table th {
	font-size: .8rem;
	color: #6c757d;
	white-space: nowrap;
}
.ged-table td {
	vertical-align: middle;
}
.ged-editable-cell {
	cursor: pointer;
	border-radius: 3px;
	padding: 1px 4px;
	margin: -1px -4px;
	transition: background .15s;
}
.ged-editable-cell:hover {
	background: rgba(0,123,255,.08);
}
.ged-tag-add-btn {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 16px;
	height: 16px;
	border-radius: 50%;
	background: rgba(0,0,0,.12);
	color: #fff;
	font-size: .6rem;
	cursor: pointer;
	margin-left: 2px;
	vertical-align: middle;
	transition: background .15s, opacity .15s;
	opacity: 0;
}
.ged-table td:hover .ged-tag-add-btn,
.ged-tag-add-btn.ged-tag-add-btn--active {
	opacity: 1;
}
.ged-tag-add-btn:hover {
	background: rgba(0,123,255,.7);
}
.ged-pencil {
	opacity: 0;
	transition: opacity .15s;
}
.ged-table td:hover .ged-pencil {
	opacity: .5;
}

/* ── Galerie ── */
.ged-gallery {
	display: grid;
	grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
	gap: .75rem;
	padding: .75rem 0;
}
.ged-gallery-card {
	transition: box-shadow .15s, transform .1s;
	border-radius: 6px;
	overflow: hidden;
}
.ged-gallery-card:hover {
	box-shadow: 0 4px 16px rgba(0,0,0,.12) !important;
	transform: translateY(-2px);
}
.ged-gallery-preview:hover {
	opacity: .9;
}
</style>
